import React, { useEffect, useState } from "react";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createEventResult,
  createMessage,
  addComentario,
  addFacturacion,
} from "../../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import {
  Col,
  Row,
  Input,
  Label,
  Button,
  Form,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Badge,
  Table,
  Spinner,
  FormGroup,
  Tooltip,
  Collapse,
} from "reactstrap";

import { getTasaDolar } from "../../../helpers/api_helper";
import { getUrlPrefirmada, validarFacturaUnica } from "../../../helpers/backend_helper";
import FileViewer from "../../../Components/Common/FileViewer";
import EstatusPagoBadge from "../../../Components/Facturacion/EstatusPagoBadge";
import TrackingPagoTooltip from "../../../Components/Facturacion/TrackingPagoTooltip";
import { validarFecha, validarMonto, tipoEfectivo } from "../../../utils/validaciones";
import { getMontoCobertura } from "../../../utils/solicitudDataCleaner";
import ValidadorImagenFactura from "../../../Components/Facturas/ValidadorImagenFactura";
import ModalMedicamentosFactura from "../../../Components/Facturas/ModalMedicamentosFactura";
import SecureStorage from "../../../helpers/secure_storage";
import { DatosSesion } from "../../../Components/Common/DatosSesion";

// Import React FilePond
import { FilePond, registerPlugin } from "react-filepond";
// Import FilePond styles
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginFileEncode from "filepond-plugin-file-encode";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";

// Register the plugins (sin transformación ni resize para mantener calidad original)
registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileEncode,
  FilePondPluginFileValidateType
);

const createBlobUrl = (base64Data) => {
  try {
    // Verificar si es null o undefined
    if (!base64Data) {
      console.error('❌ createBlobUrl - Datos vacíos');
      return null;
    }
    
    // Verificar si ya es una URL
    if (base64Data.startsWith("http")) {
      return base64Data;
    }

    // Verificar si tiene el formato correcto de data URL
    if (!base64Data.includes(";base64,")) {
      // Si no tiene el formato correcto, intentar añadirlo
      base64Data = `data:application/octet-stream;base64,${base64Data}`;
    }

    const parts = base64Data.split(";base64,");
    const contentType = parts[0].split(":")[1] || "application/octet-stream";
    
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }

    const blob = new Blob([uInt8Array], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  } catch (error) {
    console.error("❌ Error al crear blob URL:", error);
    console.error("❌ Datos recibidos:", base64Data?.substring(0, 200));
    return null;
  }
};

const compressImage = async (base64String) => {
  try {
    // Crear una imagen desde el base64
    const img = new Image();

    // Crear una promesa para manejar la carga de la imagen
    const imageLoadPromise = new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = base64String;
    });

    // Esperar a que la imagen cargue
    const loadedImg = await imageLoadPromise;

    // Crear un canvas para la compresión
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Calcular las dimensiones máximas (por ejemplo, 800px de ancho máximo)
    const MAX_WIDTH = 800;
    const scaleFactor = MAX_WIDTH / loadedImg.width;
    canvas.width = MAX_WIDTH;
    canvas.height = loadedImg.height * scaleFactor;

    // Dibujar la imagen en el canvas con las nuevas dimensiones
    ctx.drawImage(loadedImg, 0, 0, canvas.width, canvas.height);

    // Convertir el canvas a base64 con compresión
    const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);

    return compressedBase64;
  } catch (error) {
    console.error("Error en la compresión de imagen:", error);
    throw new Error(`Error al comprimir la imagen: ${error.message}`);
  }
};

const cleanBase64 = (base64String) => {
  if (!base64String) return "";
  
  // Si ya es una URL, devolverla sin modificar
  if (isURL(base64String)) {
    return base64String;
  }
  
  // Limpiar el base64
  let cleaned = base64String.split(",")[1] || base64String;
  
  // Eliminar cualquier signo = al final (padding de base64)
  // Solo si NO es una URL
  cleaned = cleaned.replace(/=+$/, "");
  
  return cleaned;
};

const isURL = (string) => {
  if (!string) return false;
  return string.startsWith("http://") || string.startsWith("https://");
};

const Facturacion = ({ solicitud }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFacturaIndex, setEditingFacturaIndex] = useState(null);
  const [monto, setMonto] = useState(0);
  const [files, setFiles] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [facturaImageUrl, setFacturaImageUrl] = useState("");
  const [showFacturacion, setShowFacturacion] = useState(null);
  const processingFileRef = React.useRef(false); // Bandera para evitar ciclo infinito
  const facturacionSectionRef = React.useRef(null); // Referencia para mantener scroll
  const [fechaFacturacion, setFechaFacturacion] = useState(null);
  const [numeroFactura, setNumeroFactura] = useState(null);
  const [saint, setSaint] = useState(null);
  const [montoTotal, setMontoTotal] = useState(null);
  const [alertaFecha, setAlertaFecha] = useState(null); // Nueva alerta para fechas fuera del mes-año actual
  const [tooltipOpen, setTooltipOpen] = useState({}); // Estado para tooltips
  const [errorMessage, setErrorMessage] = useState("");
  const [archivoAdjunto, setArchivoAdjunto] = useState(null);
  const [loadingx, setLoadingx] = useState(false);
  const [shouldProcess, setShouldProcess] = useState(false);
  const [tasaDolar, setTasaDolar] = useState(null);
  const [loadingTasa, setLoadingTasa] = useState(false);
  // Estado para validación de factura única
  const [validacionFactura, setValidacionFactura] = useState({
    estado: "pendiente", // pendiente, validando, unica, duplicada
    mensaje: "",
    facturaExistente: null,
  });
  // Estados para flujo unificado de facturación
  const [estadoFacturacion, setEstadoFacturacion] = useState("idle"); // idle, uploading, processing, success, error, manual
  const [modoManualExplicito, setModoManualExplicito] = useState(false); // true cuando el usuario elige "Ingresar Datos Manualmente"
  const [medicamentosExtraidos, setMedicamentosExtraidos] = useState([]);
  const [medicamentosColapsados, setMedicamentosColapsados] = useState(true); // Estado para colapsar medicamentos extraídos
  const [imagenFacturaPreview, setImagenFacturaPreview] = useState(null); // Preview de imagen IA
  const [imagenFacturaBase64, setImagenFacturaBase64] = useState(null); // Base64 de imagen IA para guardar
  const [errorImagenDetalle, setErrorImagenDetalle] = useState(null); // Detalles del error de imagen (dimensiones, etc.)

  // Estados para flujo de monto excedente (permiso Seguros Mercantil)
  const [montoExcedido, setMontoExcedido] = useState(false);
  const [mensajeExcedente, setMensajeExcedente] = useState("");
  const [modalExcedenteOpen, setModalExcedenteOpen] = useState(false);
  const [modalPermisoOpen, setModalPermisoOpen] = useState(false);
  const [archivoPermiso, setArchivoPermiso] = useState(null);
  const [previewPermiso, setPreviewPermiso] = useState(null);
  const [loadingPermiso, setLoadingPermiso] = useState(false);
  const pendingExcedenteRef = React.useRef(false);

  // Estados para validación de razón social
  const [razonSocialMismatch, setRazonSocialMismatch] = useState(null);
  const [modalRazonSocialOpen, setModalRazonSocialOpen] = useState(false);
  const [datosFacturaPendiente, setDatosFacturaPendiente] = useState(null);

  const nombreUsuarioFacturacion = `${DatosSesion("nombre")} ${DatosSesion("apellido")}`.trim();
  const facturaConExcedentePendiente = solicitud?.facturacion?.find(f => f.permisoExcedente?.pendiente === true);
  const permisoExcedentePendiente = !!facturaConExcedentePendiente;
  const esMercantilSolicitud = solicitud?.aseguradora === "Seguros Mercantil";
  const esHispanaSolicitud = solicitud?.aseguradora?.toLowerCase() === "hispana de seguros";
  const esRealSegurosSolicitud = solicitud?.aseguradora?.toLowerCase() === "real seguros";

  const selectLayoutState = (state) => state;
  const pageData = createSelector(selectLayoutState, (state) => ({
    siscotel: state.Siscotel.siscotel,
    error: state.Siscotel.error,
    loading: state.Siscotel.loading,
    errorMsg: state.Siscotel.errorMsg,
    colorMsg: state.Siscotel.colorMsg,
  }));

  const { siscotel, error, loading, errorMsg, colorMsg } =
    useSelector(pageData);

  /**
   * Helper para limpiar estados de error de imagen
   * @param {string} nuevoEstado - Estado al que cambiar ('idle', 'manual', etc.)
   */
  const limpiarEstadosError = (nuevoEstado = "idle") => {
    setImagenFacturaPreview(null);
    setImagenFacturaBase64(null);
    setErrorImagenDetalle(null);
    setEstadoFacturacion(nuevoEstado);
    setFiles([]);
    processingFileRef.current = false;
    // Si el nuevo estado es "manual", marcar como modo manual explícito
    if (nuevoEstado === "manual") {
      setModoManualExplicito(true);
    } else {
      setModoManualExplicito(false);
    }
  };

  /**
   * Validar razón social de la factura contra la aseguradora de la solicitud
   * @param {string} razonSocialFactura - Razón social extraída de la factura
   * @param {string} aseguradora - Aseguradora de la solicitud
   * @returns {object} { valida: boolean, mensaje: string, razonEsperada: string, razonEncontrada: string }
   */
  const validarRazonSocial = (razonSocialFactura, aseguradora) => {
    // Solo validar para MyH+ y Mercantil
    if (!aseguradora || (aseguradora !== "MyH+" && aseguradora !== "Seguros Mercantil")) {
      return { valida: true, mensaje: "", razonEsperada: "", razonEncontrada: "" };
    }

    // Si no hay razón social en la factura, no podemos validar
    if (!razonSocialFactura || razonSocialFactura.trim() === "") {
      return { valida: true, mensaje: "", razonEsperada: "", razonEncontrada: "" };
    }

    // Normalizar razón social (quitar espacios extras, convertir a mayúsculas, manejar puntuación)
    const normalizar = (str) => str
      .trim()
      .toUpperCase()
      .replace(/\s+/g, ' ')           // Múltiples espacios → uno solo
      .replace(/\s*,\s*/g, ',')       // Espacios alrededor de coma → sin espacios
      .replace(/\s*\.\s*/g, '.')      // Espacios alrededor de punto → sin espacios
      .replace(/\s+/g, ' ');          // Limpiar espacios restantes
    const razonNormalizada = normalizar(razonSocialFactura);

    // Definir razones sociales esperadas
    const razonesEsperadas = {
      "MyH+": "BECONSULT C.A.",
      "Seguros Mercantil": "MERCANTIL SEGUROS, C.A."
    };

    const razonEsperada = razonesEsperadas[aseguradora];
    const razonEsperadaNormalizada = normalizar(razonEsperada);

    // Validar si coincide (permitir variaciones como "BECONSULT C.A" sin punto, etc.)
    const coincide = razonNormalizada.includes(razonEsperadaNormalizada.replace(/\./g, '')) ||
                     razonEsperadaNormalizada.replace(/\./g, '').includes(razonNormalizada.replace(/\./g, ''));

    if (!coincide) {
      return {
        valida: false,
        mensaje: `La razón social de la factura no corresponde a ${aseguradora}`,
        razonEsperada: razonEsperada,
        razonEncontrada: razonSocialFactura
      };
    }

    return { valida: true, mensaje: "", razonEsperada: "", razonEncontrada: "" };
  };

  // Cargar la tasa del dólar al montar el componente
  useEffect(() => {
    const fetchTasaDolar = async () => {
      setLoadingTasa(true);
      try {
        const response = await getTasaDolar();
        if (response.status === 200 && response.data) {
          setTasaDolar(response.data);
        }
      } catch (error) {
        console.error("Error al obtener tasa del dólar:", error);
      }
      setLoadingTasa(false);
    };

    fetchTasaDolar();
  }, []);

  useEffect(() => {
    if (solicitud) {
      setIsLoading(false);
      
      // Lógica diferencial por aseguradora:
      // - MyH+: usa solicitud.montoCobertura (viene de Siscotel en USD)
      // - Seguros Mercantil: usa valor fijo de variable de entorno (en Bs)
      if (solicitud.aseguradora === "Seguros Mercantil") {
        // Seguros Mercantil: monto fijo en Bs
        setMonto(process.env.REACT_APP_MONTO_COBERTURA_MERCANTIL || "0");
      } else {
        // MyH+, Latina Salud y otras: usa monto de la solicitud (en USD)
        // getMontoCobertura maneja objeto con parsedValue (Latina Salud) o número directo (MyH+)
        setMonto(getMontoCobertura(solicitud.montoCobertura));
      }
      
      if (solicitud.facturacion) {
        setShowFacturacion(true);
      }
    }
  }, [solicitud]);

  // Revalidar el monto cuando cambie la tasa del dólar
  useEffect(() => {
    if (montoTotal && tasaDolar?.precio && monto) {
      const numberValue = montoTotal.replace(",", ".");
      const montoIngresado = parseFloat(numberValue) || 0;
      
      // Para Seguros Mercantil: montoCobertura ya está en Bs (no convertir)
      // Para MyH+: montoCobertura está en USD (convertir a Bs)
      const montoMaximo = esMercantilSolicitud 
        ? parseFloat(monto)  // Ya está en Bs
        : parseFloat(monto) * tasaDolar.precio;  // USD → Bs

      if (montoIngresado > montoMaximo) {
        if (esMercantilSolicitud) {
          setMontoExcedido(true);
          setMensajeExcedente(
            `El monto excede la cobertura de ${montoMaximo.toFixed(2).replace(".", ",")} Bs. Se requerirá permiso de Seguros Mercantil.`
          );
          setFormErrors((prev) => ({ ...prev, montoTotal: null }));
        } else {
          setMontoExcedido(false);
          setMensajeExcedente("");
          setFormErrors((prev) => ({
            ...prev,
            montoTotal: `El monto no puede exceder ${montoMaximo
              .toFixed(2)
              .replace(".", ",")} Bs (${monto} USD × ${tasaDolar.precio.toFixed(2)} Bs/USD)`,
          }));
        }
      } else {
        setMontoExcedido(false);
        setMensajeExcedente("");
        if (formErrors.montoTotal) {
          setFormErrors((prev) => ({ ...prev, montoTotal: null }));
        }
      }
    }
  }, [tasaDolar?.precio, montoTotal, monto, esMercantilSolicitud]);

  // Nota: La validación de número de factura se realiza al hacer clic en "Registrar Factura"
  // para garantizar que siempre se ejecute antes de permitir el registro

  const validarNumeroFacturaConParametro = async (numeroParaValidar) => {
    if (!numeroParaValidar || numeroParaValidar.trim() === "") return;

    setValidacionFactura({
      estado: "validando",
      mensaje: "Validando factura...",
      facturaExistente: null,
    });

    try {
      const response = await validarFacturaUnica({
        numero_factura: numeroParaValidar,
        codigo_proveedor: solicitud.codigoProveedor,
        aseguradora: solicitud.aseguradora,
      });

      if (response.data && response.data.data) {
        const resultado = response.data.data;
        if (resultado.unica) {
          setValidacionFactura({
            estado: "unica",
            mensaje: "Factura válida",
            facturaExistente: null,
          });
        } else {
          setValidacionFactura({
            estado: "duplicada",
            mensaje: resultado.mensaje,
            facturaExistente: resultado.factura_existente,
          });
          toast.warning(resultado.mensaje);
        }
      }
    } catch (error) {
      console.error("Error validando factura:", error);
      setValidacionFactura({
        estado: "pendiente",
        mensaje: "",
        facturaExistente: null,
      });
    }
  };

  const validarNumeroFactura = async () => {
    await validarNumeroFacturaConParametro(numeroFactura);
  };

  const handleFechaFacturacion = (event) => {
    const { value } = event.target;
    const fechaSeleccionada = value.trim();
    
    // Validar que la fecha no sea futura
    if (fechaSeleccionada) {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0); // Resetear horas para comparar solo fechas
      
      // Parse date as local time to avoid timezone issues
      const [year, month, day] = fechaSeleccionada.split('-').map(Number);
      const fechaIngresada = new Date(year, month - 1, day); // month is 0-indexed
      
      if (fechaIngresada > hoy) {
        setFormErrors((prev) => ({
          ...prev,
          fechaFacturacion: "La fecha de facturación no puede ser futura",
        }));
        setFechaEntregaError(null);
        setAlertaFecha(null); // Limpiar alerta de mes-año si hay error de fecha futura
      } else {
        setFormErrors((prev) => ({
          ...prev,
          fechaFacturacion: null,
        }));
        
        // Validar si la fecha es del mes-año actual
        const fechaActual = new Date();
        const mesActual = fechaActual.getMonth();
        const añoActual = fechaActual.getFullYear();
        
        const mesIngresado = fechaIngresada.getMonth();
        const añoIngresado = fechaIngresada.getFullYear();
        
        if (mesIngresado !== mesActual || añoIngresado !== añoActual) {
          // La fecha no es del mes-año actual
          const nombreMes = fechaIngresada.toLocaleDateString('es-VE', { month: 'long', year: 'numeric' });
          setAlertaFecha({
            tipo: 'warning',
            mensaje: `La fecha ${nombreMes} no corresponde al mes actual. ¿Desea usarla de todas formas?`,
            fecha: fechaSeleccionada,
            mesMostrado: nombreMes
          });
        } else {
          setAlertaFecha(null); // Limpiar alerta si la fecha es del mes actual
        }
        
        // Validar contra registro de entrega (solo para crónicas)
        const validation = validateFacturaDateAgainstDelivery(fechaSeleccionada);
        if (!validation.valid) {
          setFechaEntregaError(validation.message);
        } else {
          setFechaEntregaError(null);
        }
      }
    } else {
      setFechaEntregaError(null);
      setAlertaFecha(null);
    }
    
    setFechaFacturacion(fechaSeleccionada);
  };

  const handleNumeroFactura = (event) => {
    const { value } = event.target;

    // Preservar el número tal como se ingresa, incluyendo ceros a la izquierda
    setNumeroFactura(value.trim());
    // Resetear validación cuando cambia el número
    setValidacionFactura({
      estado: "pendiente",
      mensaje: "",
      facturaExistente: null,
    });
  };

  const handleSaint = (event) => {
    const { value } = event.target;
    // Para Seguros Mercantil, mantener el número exacto con ceros a la izquierda
    const saintFinal = value.trim();
    setSaint(saintFinal);
  };

  const confirmarFechaFueraMes = () => {
    // El usuario confirma usar la fecha fuera del mes-año actual
    setAlertaFecha(null);
    setFormErrors(prev => ({ ...prev, fechaFacturacion: null }));
  };

  const limpiarFecha = () => {
    // El usuario decide limpiar la fecha
    setFechaFacturacion("");
    setAlertaFecha(null);
    setFormErrors(prev => ({ ...prev, fechaFacturacion: null }));
    setFechaEntregaError(null);
  };

  // Función para manejar tooltips
  const toggleTooltip = (id) => {
    setTooltipOpen(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleMontoTotal = (e) => {
    const value = e.target.value;

    // Solo permitir números, una coma y hasta 2 decimales
    if (!/^[0-9]*,?[0-9]{0,2}$/.test(value) && value !== "") {
      return;
    }

    // Convertir el valor ingresado a número (reemplazando la coma por punto)
    const numberValue = value.replace(",", ".");
    const montoIngresado = parseFloat(numberValue) || 0;

    // Calcular el monto máximo permitido (conversión diferenciada)
    // Para Seguros Mercantil: montoCobertura ya está en Bs (no convertir)
    // Para MyH+: montoCobertura está en USD (convertir a Bs)
    const montoMaximo = esMercantilSolicitud 
      ? parseFloat(monto)  // Ya está en Bs
      : parseFloat(monto) * (tasaDolar?.precio || 0);  // USD → Bs

    if (montoIngresado > montoMaximo && montoMaximo > 0) {
      if (esMercantilSolicitud) {
        setMontoExcedido(true);
        setMensajeExcedente(
          `El monto excede la cobertura de ${montoMaximo.toFixed(2).replace(".", ",")} Bs. Se requerirá permiso de Seguros Mercantil.`
        );
        setFormErrors((prev) => ({ ...prev, montoTotal: null }));
      } else {
        setMontoExcedido(false);
        setMensajeExcedente("");
        setFormErrors((prev) => ({
          ...prev,
          montoTotal: `El monto no puede exceder ${montoMaximo
            .toFixed(2)
            .replace(".", ",")} Bs (${monto} USD × ${(tasaDolar?.precio || 0).toFixed(2)} Bs/USD)`,
        }));
      }
    } else {
      setMontoExcedido(false);
      setMensajeExcedente("");
      setFormErrors((prev) => ({ ...prev, montoTotal: null }));
    }

    setMontoTotal(value);
  };

  // Agregar un nuevo estado para manejar los errores de validación
  const [formErrors, setFormErrors] = useState({
    fechaFacturacion: false,
    numeroFactura: false,
    saint: false,
    montoTotal: false,
    files: false,
  });

  // Estado para error de validación de fecha vs registro de entrega
  const [fechaEntregaError, setFechaEntregaError] = useState(null);

  // Función de validación del formulario
  const validateForm = () => {
    const errors = {
      fechaFacturacion: false,
      numeroFactura: false,
      saint: false,
      montoTotal: false,
      files: false,
    };
    
    // Validar fecha usando función centralizada
    if (!fechaFacturacion || fechaFacturacion.trim() === "") {
      errors.fechaFacturacion = "La fecha es requerida";
    } else if (!validarFecha(fechaFacturacion)) {
      errors.fechaFacturacion = "Fecha inválida";
    }
    
    // Validar número de factura
    if (!numeroFactura || numeroFactura.trim() === "") {
      errors.numeroFactura = "El número de factura es requerido";
    } else if (numeroFactura.length < 3) {
      errors.numeroFactura = "El número de factura debe tener al menos 3 caracteres";
    }
    
    // Validar SAINT solo para Seguros Mercantil
    if (solicitud?.aseguradora === "Seguros Mercantil") {
      if (!saint || saint.trim() === "") {
        errors.saint = "El número SAINT es requerido para Seguros Mercantil";
      }
    }
    
    // Validar monto usando función centralizada
    if (!montoTotal || montoTotal.trim() === "") {
      errors.montoTotal = "El monto es requerido";
    } else {
      // Convertir formato venezolano (coma decimal) a número
      const montoNumerico = parseFloat(montoTotal.replace(/\./g, "").replace(",", "."));
      if (!validarMonto(montoNumerico, 0.01)) {
        errors.montoTotal = "El monto debe ser mayor a 0";
      }
    }
    
    // Validar archivo adjunto (puede venir de FilePond, modo IA, o edición)
    if (!files || (Array.isArray(files) && !files.length && !archivoAdjunto && !imagenFacturaPreview)) {
      errors.files = "Debe adjuntar una factura";
    }

    setFormErrors(errors);

    // Mostrar mensaje de error si hay campos inválidos
    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      const errorMessages = Object.values(errors).filter(e => e).join(". ");
      setErrorMessage(errorMessages || "Por favor, complete todos los campos requeridos");
      return false;
    }

    return true;
  };

  const handleSubmitFacturaion = async () => {
    // PASO 1: Validar número de factura ANTES de cualquier otra validación
    if (!numeroFactura || numeroFactura.trim() === "") {
      toast.error(" Por favor ingrese el número de factura", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    // Validar que el número de factura no esté duplicado
    // EXCEPCIÓN: Si estamos editando, permitir el mismo número de la factura actual
    if (!isEditing) {
      // Solo validar duplicados si es una NUEVA factura
      try {
        const response = await validarFacturaUnica({
          numero_factura: numeroFactura,
          codigo_proveedor: solicitud.codigoProveedor,
          aseguradora: solicitud.aseguradora,
        });

        if (response.data && response.data.data) {
          const resultado = response.data.data;
          if (!resultado.unica) {
            // Factura duplicada - BLOQUEAR registro
            toast.error(
              `❌ ${resultado.mensaje}\n\nNo se puede registrar esta factura.`,
              {
                position: "top-center",
                autoClose: 8000,
              }
            );
            
            // Actualizar estado de validación para mostrar visualmente
            setValidacionFactura({
              estado: "duplicada",
              mensaje: resultado.mensaje,
              facturaExistente: resultado.factura_existente,
            });
            
            return; // DETENER el proceso de registro
          }
          
          // Factura única - actualizar estado y continuar
          setValidacionFactura({
            estado: "unica",
            mensaje: "Factura válida",
            facturaExistente: null,
          });
        }
      } catch (error) {
        console.error("Error validando factura:", error);
        toast.error(
          " Error al validar el número de factura. Por favor intente nuevamente.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        return; // DETENER si hay error en la validación
      }
    } else {
      // Modo EDICIÓN: Verificar si el número cambió
      const facturaOriginal = solicitud.facturacion[editingFacturaIndex];
      const numeroOriginal = facturaOriginal?.numeroFactura;
      
      if (numeroFactura !== numeroOriginal) {
        // El número cambió - validar que el NUEVO número no esté duplicado
        try {
          const response = await validarFacturaUnica({
            numero_factura: numeroFactura,
            codigo_proveedor: solicitud.codigoProveedor,
            aseguradora: solicitud.aseguradora,
          });

          if (response.data && response.data.data) {
            const resultado = response.data.data;
            if (!resultado.unica) {
              // Nuevo número duplicado - BLOQUEAR
              toast.error(
                `❌ ${resultado.mensaje}\n\nNo se puede usar este número de factura.`,
                {
                  position: "top-center",
                  autoClose: 8000,
                }
              );
              
              setValidacionFactura({
                estado: "duplicada",
                mensaje: resultado.mensaje,
                facturaExistente: resultado.factura_existente,
              });
              
              return; // DETENER
            }
            
            // Nuevo número es único - continuar
            setValidacionFactura({
              estado: "unica",
              mensaje: "Factura válida",
              facturaExistente: null,
            });
          }
        } catch (error) {
          console.error("Error validando factura:", error);
          toast.error(
            " Error al validar el número de factura. Por favor intente nuevamente.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
          return; // DETENER si hay error
        }
      }
      // Si el número NO cambió, no validar (es la misma factura)
    }

    // PASO 2: Validar que la solicitud tenga diagnóstico registrado
    // Para Latina Salud: verificar diagnóstico pre-asignado
    // Para otras aseguradoras: verificar porcentaje de avance
    const tieneDiagnostico = (() => {
      const aseg = solicitud?.aseguradora?.toLowerCase();
      if (aseg === "latina salud") {
        return solicitud.diagnostico && solicitud.diagnostico.trim() !== "";
      }
      if (aseg === "hispana de seguros" || aseg === "real seguros") {
        // Diagnóstico pre-asignado o diagnóstico farmacológico registrado en-app
        return (solicitud.diagnostico && solicitud.diagnostico.trim() !== "") ||
               (solicitud.porcentaje_avance && solicitud.porcentaje_avance >= 20);
      }
      return solicitud.porcentaje_avance && solicitud.porcentaje_avance >= 20;
    })();
    
    if (!tieneDiagnostico) {
      toast.error(
        "No se puede registrar facturación sin diagnóstico farmacológico. " +
        "Por favor, registre el diagnóstico primero.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    if (tipoEfectivo(solicitud) === "Cronica") {
      const tieneMedicamentosEsteMes = () => {
        if (!solicitud.cronico?.historialEntregas?.length) return false;
        
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        
        return solicitud.cronico.historialEntregas.some((entrega) => {
          const entregaDate = new Date(entrega.fecha);
          return (
            entregaDate.getMonth() === currentMonth &&
            entregaDate.getFullYear() === currentYear &&
            entrega.medicamentos?.length > 0
          );
        });
      };

      if (!tieneMedicamentosEsteMes()) {
        toast.error(
          "No se puede registrar facturación sin medicamentos registrados este mes. " +
          "Por favor, registre los medicamentos primero en la sección de 'Registro de Medicamentos'.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        return;
      }
    }
    
    // Validar el formulario
    if (!validateForm()) {
      return;
    }

    // Si el monto excede la cobertura (Mercantil), pedir confirmación
    if (montoExcedido && !pendingExcedenteRef.current) {
      setModalExcedenteOpen(true);
      return;
    }

    const esExcedente = pendingExcedenteRef.current;
    pendingExcedenteRef.current = false;

    // Solo proceder si no está ya en proceso
    if (!loadingx) {
      setLoadingx(true);
    }

    let miFactura = null;

    try {
      // Prioridad 1: Imagen procesada con IA (usar base64 guardado)
      if (imagenFacturaBase64) {
        // Usar el base64 que ya fue procesado y guardado
        miFactura = imagenFacturaBase64;
      } else if (files && files.length > 0) {
        // Prioridad 2: Archivo subido manualmente (sin IA)
        const file = files[0];
        
        const fileData = file.getFileEncodeDataURL();

        if (!fileData) {
          // Intentar obtener base64 de otra forma
          const base64FromReader = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file.file);
          });
          
          if (base64FromReader) {
            miFactura = base64FromReader;
            setArchivoAdjunto(base64FromReader);
          } else {
            throw new Error("No se pudieron obtener los datos del archivo");
          }
        } else if (file.fileType === "application/pdf") {
          // Para PDFs, usa directamente el fileData sin comprimir
          setArchivoAdjunto(fileData);
          miFactura = fileData;
        } else {
          // Para imágenes, usa la compresión existente
          try {
            const compressedData = await compressImage(fileData);
            setArchivoAdjunto(compressedData);
            miFactura = compressedData;
          } catch (compressionError) {
            console.error("Error en la compresión:", compressionError);
            throw compressionError;
          }
        }
      } else if (isEditing && editingFacturaIndex !== null && archivoAdjunto) {
        // Si estamos editando y hay un archivo adjunto guardado pero no uno nuevo
        //console.log('✅ Usando archivoAdjunto existente');
        // Verificar si el archivo adjunto es una URL
        if (isURL(archivoAdjunto)) {
          // Si es una URL, no la procesamos, la usamos directamente
          miFactura = archivoAdjunto;
        } else {
          // Si es base64, usarlo normalmente
          miFactura = archivoAdjunto;
        }
      } else {
        console.warn(' NINGUNA condición cumplida para obtener imagen de factura');
      }

      // Continuar con el proceso de facturación...
      const formatearFecha = (fecha) => {
        if (!fecha) return null;
        const [year, month, day] = fecha.split("-");
        return `${day}-${month}-${year}`;
      };

      const facturacion = {
        IdentificadorEvento: solicitud.identificador,
        TipoAccion: (() => {
          // TipoAccion ahora siempre usa formato MM-YYYY de la fecha de factura
          if (!fechaFacturacion) return "";
          // Extraer directamente año, mes, día para evitar problemas de zona horaria
          const [year, month, day] = fechaFacturacion.split("-");
          // Asegurar formato MM con cero al inicio si es necesario
          const formattedMonth = month.padStart(2, "0");
          return `${formattedMonth}-${year}`;
        })(),
        CodigoDiagnostico: solicitud.codigoDiagnosticoFarmacologico || "",
        SubCodigoDiagnostico: solicitud.codigoSubdiagnosticoFarmacologico || "",
        Procedimiento: "Servicio de medicamentos",
        FechaEvento: formatearFecha(fechaFacturacion),
        MontoFactura: String(montoTotal).replace(".", ","),
        MonedaFactura: "B",
        Factura: miFactura && !isURL(miFactura) ? cleanBase64(miFactura) : "",
        NumeroFactura: numeroFactura,
        CodigoProveedor: solicitud.codigoProveedor || "",
      };

      const esMercantil = solicitud.aseguradora === "Seguros Mercantil";
      const esLatinaSalud = solicitud?.aseguradora?.toLowerCase() === "latina salud";
      const esHispana = solicitud?.aseguradora?.toLowerCase() === "hispana de seguros";
      const esRealSeguros = solicitud?.aseguradora?.toLowerCase() === "real seguros";

      if (esMercantil || esLatinaSalud || esHispana || esRealSeguros) {
        // Procesar localmente para Seguros Mercantil, Latina Salud, Hispana y Real Seguros
        await procesarActualizacionLocal(miFactura, esExcedente);
      } else {
        // Enviar la petición a SISCOTEL solo para MyH+
        // El thunk ahora valida automáticamente el status de Siscotel y lanza excepción si hay error
        await dispatch(createEventResult(facturacion));
        await procesarActualizacionLocal(miFactura, false);
      }
    } catch (error) {
      // Mostrar mensaje de error específico de MyH+ si está disponible
      if (error.siscotelError) {
        toast.error(`Error en MyH+: ${error.message}`, {
          autoClose: 5000
        });
      } else {
        toast.error("Error al procesar la factura. Por favor, intente nuevamente.", {
          autoClose: 5000
        });
      }
      
      setLoadingx(false);
    }
  };

  // Nueva función para procesar la actualización local después de SISCOTEL
  const procesarActualizacionLocal = async (miFactura, esExcedente = false) => {
    try {
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      // Obtener la tasa del dólar actualizada antes de calcular
      let tasaActual = tasaDolar?.precio;
      try {
        const tasaResponse = await getTasaDolar();
        if (tasaResponse?.status === 200 && tasaResponse?.data?.precio) {
          tasaActual = tasaResponse.data.precio;
          // Actualizar el estado para futuras referencias
          setTasaDolar(tasaResponse.data);
        } else {
          console.warn(" No se pudo obtener tasa actualizada, usando tasa cargada:", tasaActual);
        }
      } catch (error) {
        console.error("❌ Error al obtener tasa actualizada:", error);
      }

      // Procesar el archivo adjunto si existe
      // Preservar el número de factura tal como se ingresó, incluyendo ceros a la izquierda
      let facturaData = {
        fecha: fechaFacturacion,
        numeroFactura: numeroFactura.trim(),
        saint: saint || null,
        montoTotal: parseFloat(montoTotal.replace(",", ".")),
        monedaFactura: "B",
        montoDolar: parseFloat(montoTotal.replace(",", ".")) / tasaActual,
        medicamentos: medicamentosExtraidos || []
      };

      // Si el monto excede cobertura, marcar la factura con permiso pendiente
      if (esExcedente) {
        facturaData.permisoExcedente = {
          pendiente: true,
          fechaSolicitud: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString(),
          montoMaximoBs: parseFloat((parseFloat(monto) * tasaActual).toFixed(2)),
          usuarioSolicitante: nombreUsuarioFacturacion,
        };
      }

      // Asignar el archivo adjunto - usamos directamente el mismo que se envió a SISCOTEL
      if (miFactura) {
        // Si miFactura ya es una URL, no procesarla
        if (isURL(miFactura)) {
          facturaData.adjunto = miFactura;
        } else {
          // Si es base64, limpiarlo completamente
          facturaData.adjunto = cleanBase64(miFactura);
        }
      } else if (archivoAdjunto) {
        // Si archivoAdjunto ya es una URL, no procesarla
        if (isURL(archivoAdjunto)) {
          facturaData.adjunto = archivoAdjunto;
        } else {
          // Si es base64, limpiarlo completamente
          facturaData.adjunto = cleanBase64(archivoAdjunto);
        }
      }

      // Crear el array de facturación
      let facturacionArray = [];

      // Si ya existe facturación previa, la incluimos
      if (solicitud.facturacion && Array.isArray(solicitud.facturacion)) {
        facturacionArray = [...solicitud.facturacion];
      }

      if (isEditing && editingFacturaIndex !== null) {
        // Modo edición: actualizar la factura existente

        // Asegurarse de que el índice es válido
        if (
          editingFacturaIndex >= 0 &&
          editingFacturaIndex < facturacionArray.length
        ) {
          facturacionArray[editingFacturaIndex] = {
            ...facturacionArray[editingFacturaIndex],
            ...facturaData,
          };
        } else {
          console.error("Índice de factura inválido:", editingFacturaIndex);
        }
      } else {
        // Modo creación: agregar nueva factura
        facturacionArray.push(facturaData);
      }

      // Preparar datos de factura para el comentario
      // Formatear fecha preservando el día correcto (evitar problemas de timezone)
      let fechaFormateada = 'N/A';
      if (fechaFacturacion) {
        // Si viene en formato YYYY-MM-DD (del input type="date")
        if (/^\d{4}-\d{2}-\d{2}$/.test(fechaFacturacion)) {
          const [año, mes, dia] = fechaFacturacion.split('-');
          fechaFormateada = `${dia}/${mes}/${año}`;
        } else {
          // Fallback para otros formatos
          const fecha = new Date(fechaFacturacion);
          if (!isNaN(fecha)) {
            fechaFormateada = fecha.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
          }
        }
      }
      
      const montoFormateado = montoTotal 
        ? parseFloat(montoTotal.replace(',', '.')).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '0,00';
      const numeroFacturaLimpio = numeroFactura || 'N/A';
      
      const datosFacturaMsg = `Fecha: ${fechaFormateada}, Monto: ${montoFormateado} Bs, N°: ${numeroFacturaLimpio}`;
      
      const comentario = {
        tipo: "Enviado",
        mensaje: isEditing
          ? `Factura actualizada - ${datosFacturaMsg}`
          : `Factura registrada - ${datosFacturaMsg}`,
        estatus: true,
        adjunto: null,
        creado: creado,
      };

      const esMyH = solicitud.aseguradora === "MyH+";

      if (esMyH) {
        const message = {
          IdentificadorEvento: solicitud.identificador,
          Tipo: "Mensaje",
          Mensaje: comentario.mensaje,
          Adjunto: "",
          CodigoProveedor: solicitud.codigoProveedor,
        };
        // Enviar las actualizaciones secuencialmente en lugar de en paralelo
        // Enviar el mensaje a SISCOTEL solo para MyH+
        await dispatch(createMessage(message));
      }

      // Luego actualizar la facturación
      await dispatch(
        addFacturacion({
          identificador: String(solicitud.identificador),
          facturacionArray,
        })
      );

      // Finalmente actualizar el comentario
      await dispatch(
        addComentario({
          identificador: String(solicitud.identificador),
          comentario,
        })
      );

      // Si el monto excede la cobertura, registrar en bitácora
      if (esExcedente) {
        const montoFacturadoBs = facturaData.montoTotal;
        const montoMaximoBs = facturaData.permisoExcedente?.montoMaximoBs || 0;

        const comentarioPermiso = {
          tipo: "Enviado",
          mensaje: `Monto excedente: Factura por ${montoFacturadoBs.toFixed(2)} Bs supera cobertura máxima de ${montoMaximoBs.toFixed(2).replace(".", ",")} Bs. Se debe solicitar permiso por correo a Seguros Mercantil para procesar esta solicitud.`,
          estatus: true,
          adjunto: null,
          creado: creado,
        };
        await dispatch(
          addComentario({
            identificador: String(solicitud.identificador),
            comentario: comentarioPermiso,
          })
        );
      }

      resetForm();

      if (esExcedente) {
        toast.warning(
          "Facturación registrada. Se requiere permiso de Seguros Mercantil por monto excedente. Envíe un correo solicitando autorización.",
          { autoClose: 8000 }
        );
      } else {
        toast.success("Facturación registrada exitosamente", {});
      }
      
      // Hacer scroll a la sección "Información para el Delivery" después de guardar facturación
      setTimeout(() => {
        const deliveryCard = document.querySelector('.delivery-info-card');
        if (deliveryCard) {
          deliveryCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
      
      setLoadingx(false);
    } catch (error) {
      console.error("❌ Error en el proceso de actualización local:", error);
      setLoadingx(false);
    }
  };

  const resetForm = () => {
    setFechaFacturacion("");
    setNumeroFactura("");
    setSaint("");
    setMontoTotal("");
    setFiles([]);
    setIsEditing(false);
    setEditingFacturaIndex(null);
    setShowFacturacion(true);
    setEstadoFacturacion("manual");
    setImagenFacturaPreview(null); // Limpiar preview de IA
    setImagenFacturaBase64(null); // Limpiar base64 de IA
    setAlertaFecha(null); // Limpiar alerta de fecha
    setMedicamentosExtraidos([]); // Limpiar medicamentos extraídos
    setMedicamentosColapsados(true); // Resetear a colapsado
    setFormErrors({
      fechaFacturacion: false,
      numeroFactura: false,
      saint: false,
      montoTotal: false,
      files: false,
    });
    setFechaEntregaError(null); // Limpiar error de validación de entrega
    processingFileRef.current = false; // Resetear bandera de procesamiento
    setMontoExcedido(false);
    setMensajeExcedente("");
    pendingExcedenteRef.current = false;
  };

  // Handler para confirmar factura con monto excedente
  const handleConfirmExcedente = () => {
    setModalExcedenteOpen(false);
    pendingExcedenteRef.current = true;
    handleSubmitFacturaion();
  };

  // Handler para archivo de permiso (captura correo respuesta)
  const handleArchivoPermiso = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setArchivoPermiso(null);
      setPreviewPermiso(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo no debe superar 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setArchivoPermiso(event.target.result);
      if (file.type.startsWith("image/")) {
        setPreviewPermiso(event.target.result);
      } else {
        setPreviewPermiso(null);
      }
    };
    reader.readAsDataURL(file);
  };

  // Handler para confirmar permiso recibido (subir captura)
  const handleSubirPermiso = async () => {
    if (!archivoPermiso) {
      toast.error("Debe adjuntar la captura del correo de aprobación");
      return;
    }

    if (!solicitud.facturacion || !solicitud.facturacion.length) {
      toast.error("No se encontró facturación en la solicitud");
      return;
    }

    const facturaIndex = solicitud.facturacion.findIndex(f => f.permisoExcedente?.pendiente === true);
    if (facturaIndex === -1) {
      toast.error("No se encontró factura con permiso pendiente");
      return;
    }

    setLoadingPermiso(true);

    try {
      const now = new Date();
      const creado = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();

      let adjuntoProcessed = archivoPermiso;
      if (archivoPermiso.startsWith("data:image")) {
        try {
          adjuntoProcessed = await compressImage(archivoPermiso);
        } catch {
          // Usar original si falla la compresión
        }
      }
      adjuntoProcessed = cleanBase64(adjuntoProcessed);

      // Registrar comentario con la captura del correo de aprobación
      const comentarioAprobacion = {
        tipo: "Enviado",
        mensaje: `Permiso de Seguros Mercantil recibido para monto excedente. Captura del correo de aprobación adjunta. Confirmado por: ${nombreUsuarioFacturacion}.`,
        estatus: true,
        adjunto: adjuntoProcessed,
        creado: creado,
      };

      await dispatch(
        addComentario({
          identificador: String(solicitud.identificador),
          comentario: comentarioAprobacion,
        })
      );

      // Actualizar la factura marcando el permiso como aprobado
      const facturacionActualizada = [...solicitud.facturacion];
      facturacionActualizada[facturaIndex] = {
        ...facturacionActualizada[facturaIndex],
        permisoExcedente: {
          ...facturacionActualizada[facturaIndex].permisoExcedente,
          pendiente: false,
          fechaAprobacion: creado,
          aprobadoPor: nombreUsuarioFacturacion,
        }
      };

      await dispatch(
        addFacturacion({
          identificador: String(solicitud.identificador),
          facturacionArray: facturacionActualizada,
        })
      );

      setModalPermisoOpen(false);
      setArchivoPermiso(null);
      setPreviewPermiso(null);
      toast.success(
        "Permiso de Seguros Mercantil registrado exitosamente. El flujo puede continuar con normalidad.",
        { autoClose: 5000 }
      );
    } catch (error) {
      console.error("Error al registrar permiso:", error);
      toast.error("Error al registrar el permiso. Intente nuevamente.");
    } finally {
      setLoadingPermiso(false);
    }
  };

  /**
   * Handler cuando la IA completa la validación y extracción
   */
  const handleValidacionIA = async (resultado) => {
    
    // Solo procesar si el usuario confirmó usar la imagen
    if (!resultado.confirmado) {
      return;
    }
    
    // Guardar imagen para preview Y para enviar (antes de procesar datos)
    if (resultado.imagenParaSubir) {
      // imagenParaSubir puede ser un File o un string base64
      if (resultado.imagenParaSubir instanceof File) {
        // Convertir File a base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(resultado.imagenParaSubir);
        });
        setImagenFacturaPreview(base64);
        setImagenFacturaBase64(base64);
      } else if (typeof resultado.imagenParaSubir === 'string') {
        // Ya es base64
        setImagenFacturaPreview(resultado.imagenParaSubir);
        setImagenFacturaBase64(resultado.imagenParaSubir);
      }
    }
    
    // Si la extracción fue exitosa, pre-llenar formulario
    if (resultado.success && resultado.datos) {
      const { datos } = resultado;
      
      // Convertir fecha DD-MM-YYYY → YYYY-MM-DD para input type="date"
      // NOTA: En modo edición NO se modifica la fecha - debe mantenerse la original
      if (datos.fecha && !isEditing) {
        try {
          const [dia, mes, año] = datos.fecha.split('-');
          let añoCorregido = año;
          
          // Validar si la fecha es futura y corregir automáticamente
          const fechaExtraida = new Date(`${año}-${mes}-${dia}`);
          const hoy = new Date();
          hoy.setHours(0, 0, 0, 0);
          
          if (fechaExtraida > hoy) {
            // Si la fecha es futura, asumir que es del año anterior
            añoCorregido = String(parseInt(año) - 1);
            toast.warning(
              ` Fecha corregida: ${dia}/${mes}/${añoCorregido} (la IA detectó ${año})`,
              { position: "top-right", autoClose: 5000 }
            );
          }
          
          const fechaFormateada = `${añoCorregido}-${mes}-${dia}`;
          setFechaFacturacion(fechaFormateada);
          
          // Validar si la fecha está fuera del mes actual (misma lógica que handleFechaFacturacion)
          // Parse date as local time to avoid timezone issues
          const fechaIngresada = new Date(parseInt(añoCorregido), parseInt(mes) - 1, parseInt(dia));
          const hoyValidacion = new Date();
          const mesActual = hoyValidacion.getMonth();
          const añoActual = hoyValidacion.getFullYear();
          const mesIngresado = fechaIngresada.getMonth();
          const añoIngresado = fechaIngresada.getFullYear();
          
          if (mesIngresado !== mesActual || añoIngresado !== añoActual) {
            // La fecha no es del mes-año actual
            const nombreMes = fechaIngresada.toLocaleDateString('es-VE', { month: 'long', year: 'numeric' });
            setAlertaFecha({
              tipo: 'warning',
              mensaje: `La fecha ${nombreMes} no corresponde al mes actual. ¿Desea usarla de todas formas?`,
              fecha: fechaFormateada,
              mesMostrado: nombreMes
            });
          } else {
            setAlertaFecha(null); // Limpiar alerta si la fecha es del mes actual
          }
          
          // Validar contra registro de entrega
          const validation = validateFacturaDateAgainstDelivery(fechaFormateada);
          if (!validation.valid) {
            setFechaEntregaError(validation.message);
            toast.error(
              `❌ ${validation.message}`,
              { position: "top-right", autoClose: 7000 }
            );
          } else {
            setFechaEntregaError(null);
          }
        } catch (e) {
          console.warn(' Error parseando fecha:', e);
        }
      }
      
      // Pre-llenar número de factura (preservar ceros a la izquierda)
      if (datos.numero_factura) {
        setNumeroFactura(datos.numero_factura);
      }
      
      // Pre-llenar SAINT (preservar ceros a la izquierda)
      if (datos.saint) {
        setSaint(datos.saint);
      }
      
      // Pre-llenar monto (convertir punto a coma para formato venezolano)
      if (datos.total_bolivares) {
        setMontoTotal(datos.total_bolivares.toString().replace('.', ','));
      }
      
      // Guardar medicamentos extraídos y marcar como extraídos con IA
      if (datos.medicamentos && Array.isArray(datos.medicamentos)) {
        const medicamentosConFlag = datos.medicamentos.map(med => ({
          ...med,
          extraido_con_ia: true
        }));
        setMedicamentosExtraidos(medicamentosConFlag);
      }
      
      // Validar razón social de la factura
      const validacion = validarRazonSocial(datos.razon_social_cliente, solicitud?.aseguradora);
      
      if (!validacion.valida) {
        // Hay un mismatch de razón social - guardar datos y mostrar modal de confirmación
        setRazonSocialMismatch(validacion);
        setDatosFacturaPendiente(datos);
        setModalRazonSocialOpen(true);
        setEstadoFacturacion("success"); // Mostrar formulario pero con advertencia
        
        toast.warning(
          ` ${validacion.mensaje}. Por favor, revisa la información.`,
          { position: "top-right", autoClose: 6000 }
        );
      } else {
        // Razón social válida o no aplica validación
        setRazonSocialMismatch(null);
        setEstadoFacturacion("success");
      }
      
      toast.success(
        `Datos extraídos: ${datos.medicamentos?.length || 0} medicamentos. Revisa y confirma.`,
        { position: "top-right", autoClose: 5000 }
      );
      
      // Cambiar a estado success para mostrar formulario con datos
      // setEstadoFacturacion("success");
      
      // Mantener scroll en la sección de facturación
      setTimeout(() => {
        if (facturacionSectionRef.current) {
          facturacionSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    } else {
      // Si falló la extracción, verificar si es error de imagen pequeña
      
      if (resultado.error && (resultado.error.toLowerCase().includes('imagen pequeña') || resultado.error.toLowerCase().includes('imagen muy pequeña'))) {
        // Extraer dimensiones del mensaje del backend si están disponibles
        const dimensionesMatch = resultado.error.match(/(\d+)x(\d+)px/);
        const megapixelsMatch = resultado.error.match(/([\d.]+)MP/);
        
        // Guardar detalles del error para mostrar debajo de la vista previa
        setErrorImagenDetalle({
          tipo: 'imagen_pequeña',
          dimensiones: dimensionesMatch ? `${dimensionesMatch[1]}x${dimensionesMatch[2]}px` : 'Desconocidas',
          tamaño: 'N/A',
          megapixels: megapixelsMatch ? `${megapixelsMatch[1]}MP` : 'N/A',
          mensajeBackend: resultado.error
        });
        
        // Guardar preview de la imagen si está disponible
        if (resultado.imagenParaSubir) {
          if (resultado.imagenParaSubir instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => setImagenFacturaPreview(e.target.result);
            reader.readAsDataURL(resultado.imagenParaSubir);
          } else if (typeof resultado.imagenParaSubir === 'string') {
            setImagenFacturaPreview(resultado.imagenParaSubir);
          }
        }
        
        // Cambiar a estado error para mostrar mensaje debajo de preview
        setEstadoFacturacion('error');
        
        // Toast corto y simple
        toast.error(' Imagen de baja calidad. Ver detalles debajo.', { autoClose: 4000 });
      } else {
        // Otros errores: limpiar preview y mostrar mensaje genérico
        setImagenFacturaPreview(null);
        setImagenFacturaBase64(null);
        setErrorImagenDetalle(null);
        
        toast.warning(
          ' No se pudieron extraer datos automáticamente. Ingresa los datos manualmente.',
          { position: "top-right", autoClose: 5000 }
        );
        // Cambiar a modo manual para permitir ingreso manual
        setEstadoFacturacion("manual");
      }
    }
  };

  const editarSolicitudFacturacion = (index) => {
    // Si recibimos un objeto factura en lugar de un índice, encontrar su índice
    if (typeof index === "object") {
      const facturaObj = index;
      index = solicitud.facturacion.findIndex(
        (f) =>
          f.numeroFactura === facturaObj.numeroFactura &&
          f.fecha === facturaObj.fecha
      );

      if (index === -1) {
        console.error("No se pudo encontrar la factura para editar");
        return;
      }
    }

    setIsEditing(true);
    setEditingFacturaIndex(index);
    setEstadoFacturacion("manual"); // Establecer estado para mostrar formulario
    setModoManualExplicito(false); // En edición normal, NO es modo manual explícito

    if (solicitud.facturacion && solicitud.facturacion[index]) {
      const facturaSeleccionada = solicitud.facturacion[index];

      // Asegurarse de que todos los valores sean válidos
      if (
        !facturaSeleccionada.fecha ||
        !facturaSeleccionada.numeroFactura ||
        !facturaSeleccionada.montoTotal
      ) {
        console.error("Datos de factura inválidos");
        return;
      }

      // Formatear la fecha para el input type="date"
      const fecha = facturaSeleccionada.fecha
        ? facturaSeleccionada.fecha.split("T")[0]
        : "";

      setFechaFacturacion(fecha);
      // Preservar el número tal como está almacenado, incluyendo ceros a la izquierda
      setNumeroFactura(facturaSeleccionada.numeroFactura);
      setSaint(facturaSeleccionada.saint || "");
      setMontoTotal(String(facturaSeleccionada.montoTotal).replace(".", ","));

      // Cargar medicamentos si existen
      if (facturaSeleccionada.medicamentos && Array.isArray(facturaSeleccionada.medicamentos)) {
        setMedicamentosExtraidos(facturaSeleccionada.medicamentos);
      } else {
        setMedicamentosExtraidos([]);
      }

      // Guardar referencia al archivo adjunto existente (no cargar en FilePond)
      if (facturaSeleccionada.adjunto) {
        setArchivoAdjunto(facturaSeleccionada.adjunto);
      } else {
        setArchivoAdjunto(null);
      }
      
      // Limpiar FilePond para permitir subir nueva imagen
      setFiles([]);
    }

    setShowFacturacion(false);
  };

  const FacturaModal = ({ isOpen, toggle, imageUrl }) => {
    // Verificar si es una URL
    const isUrl = imageUrl && imageUrl.startsWith("http");

    // Verificar si es PDF
    const isPDF = isUrl
      ? imageUrl.toLowerCase().endsWith(".pdf") ||
        imageUrl.toLowerCase().includes("/pdf/") ||
        imageUrl.toLowerCase().includes("content-type=application%2Fpdf")
      : imageUrl &&
        (imageUrl.includes("data:application/pdf") ||
          imageUrl.startsWith("data:application/pdf") ||
          imageUrl.includes("JVBERi0"));

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>Factura</ModalHeader>
        <ModalBody>
          {imageUrl ? (
            isPDF ? (
              <iframe
                src={imageUrl}
                style={{ width: "100%", height: "500px" }}
                title="PDF Viewer"
              />
            ) : (
              <img
                src={imageUrl}
                alt="Factura"
                style={{ width: "100%" }}
                onError={(e) => {
                  console.error("Error al cargar la imagen:", e);
                }}
              />
            )
          ) : (
            <div className="text-center">
              <p>No hay imagen disponible</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  };

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  const isValidSolicitud = () => {
    if (tipoEfectivo(solicitud) === "Aguda") {
      return getMontoCobertura(solicitud.montoCobertura) > 0;
    }

    if (solicitud.estatus !== "Nueva Solicitud" && solicitud.estatus !== "Virtual") return false;
    if (!solicitud.cronico?.fechaVigencia) return false;

    const fechaVigencia = new Date(solicitud.cronico.fechaVigencia);
    const today = new Date();

    return (
      fechaVigencia.getFullYear() > today.getFullYear() ||
      (fechaVigencia.getFullYear() === today.getFullYear() &&
        fechaVigencia.getMonth() >= today.getMonth())
    );
  };

  const hasDeliveryThisMonth = () => {
    if (tipoEfectivo(solicitud) === "Aguda") return false;
    if (!solicitud.cronico?.historialEntregas?.length) return false;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    return solicitud.cronico.historialEntregas.some((entrega) => {
      const entregaDate = new Date(entrega.fecha);
      return (
        entregaDate.getMonth() === currentMonth &&
        entregaDate.getFullYear() === currentYear
      );
    });
  };

  const hasInvoiceThisMonth = () => {
    if (tipoEfectivo(solicitud) === "Aguda") return false;
    if (!solicitud.facturacion?.length) return false;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    return solicitud.facturacion.some((factura) => {
      const facturaDate = new Date(factura.fecha);
      return (
        facturaDate.getMonth() === currentMonth &&
        facturaDate.getFullYear() === currentYear
      );
    });
  };

  const hasEntryThisMonth = () => {
    // Mantener compatibilidad con código existente
    // Retorna true si ya hay factura del mes actual
    return hasInvoiceThisMonth();
  };

  const validateFacturaDateAgainstDelivery = (fechaFactura) => {
    if (tipoEfectivo(solicitud) !== "Cronica" || isEditing) {
      return { valid: true };
    }

    if (!fechaFactura) {
      return { valid: false, message: "La fecha es requerida" };
    }

    const parseFacturaYearMonth = (value) => {
      if (!value || typeof value !== "string") return null;

      // Formato estándar del input type="date": YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m] = value.split("-").map(Number);
        if (!y || !m) return null;
        return { year: y, month: m - 1 };
      }

      // Formato alterno (si llega desde algún flujo): DD/MM/YYYY
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        const [, m, y] = value.split("/").map(Number);
        if (!y || !m) return null;
        return { year: y, month: m - 1 };
      }

      return null;
    };

    const parsedFactura = parseFacturaYearMonth(fechaFactura);
    if (!parsedFactura) {
      return { valid: false, message: "Fecha inválida" };
    }

    const facturaMonth = parsedFactura.month;
    const facturaYear = parsedFactura.year;

    // Verificar si hay registro de entrega para ese mes/año
    if (!solicitud.cronico?.historialEntregas?.length) {
      return { 
        valid: false, 
        message: "No hay registros de entrega disponibles" 
      };
    }

    const entregaDelMes = solicitud.cronico.historialEntregas.find(entrega => {
      const entregaDate = new Date(entrega.fecha);
      return entregaDate.getUTCMonth() === facturaMonth && 
             entregaDate.getUTCFullYear() === facturaYear;
    });

    if (!entregaDelMes) {
      // Obtener todos los meses disponibles de registros de entrega
      const mesesDisponibles = solicitud.cronico.historialEntregas
        .map(e => {
          const fecha = new Date(e.fecha);
          const y = fecha.getUTCFullYear();
          const m = fecha.getUTCMonth();
          const displayFecha = new Date(Date.UTC(y, m, 1));
          return {
            fecha: displayFecha,
            texto: displayFecha.toLocaleDateString('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' })
          };
        })
        .sort((a, b) => b.fecha - a.fecha) // Más reciente primero
        .map(m => m.texto);
      
      // Eliminar duplicados
      const mesesUnicos = [...new Set(mesesDisponibles)];
      
      const mensajeMeses = mesesUnicos.length === 1 
        ? mesesUnicos[0]
        : mesesUnicos.join(', ');

      return { 
        valid: false, 
        message: `La fecha de la factura debe corresponder a un mes con registro de entrega: ${mensajeMeses}`,
        entregaFecha: new Date(solicitud.cronico.historialEntregas[0].fecha)
      };
    }

    return { valid: true, entregaFecha: new Date(entregaDelMes.fecha) };
  };

  const getNextAvailableDate = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusMessage = () => {
    // Validar antigüedad de la cobertura (para ambos tipos)
    if (solicitud.fechaConsultaCobertura) {
      const fechaCobertura = new Date(solicitud.fechaConsultaCobertura);
      const hoy = new Date();
      const diasDesdeConsulta = Math.floor((hoy - fechaCobertura) / (1000 * 60 * 60 * 24));
      
      if (diasDesdeConsulta > 7) {
        return {
          title: " Cobertura desactualizada",
          message: `La cobertura fue consultada hace ${diasDesdeConsulta} días. Se recomienda registrar nuevamente los medicamentos para actualizar la cobertura antes de facturar.`,
          type: "warning"
        };
      }
    }

    if (tipoEfectivo(solicitud) === "Aguda") {
      if (getMontoCobertura(solicitud.montoCobertura) <= 0) {
        return {
          title: "Solicitud no disponible",
          message: "Esta solicitud no tiene monto de cobertura asignado.",
        };
      }
      return null;
    }

    // Lógica para Crónicas reales (MyH+)
    if (solicitud.estatus !== "Nueva Solicitud" && solicitud.estatus !== "Virtual") {
      return {
        title: "Solicitud no disponible",
        message: "Esta solicitud no está en estado 'Nueva Solicitud' o 'Virtual'.",
      };
    }

    if (!solicitud.cronico?.fechaVigencia) {
      return {
        title: "Solicitud sin fecha de vigencia",
        message: "Esta solicitud no tiene una fecha de vigencia establecida.",
      };
    }

    const fechaVigencia = new Date(solicitud.cronico.fechaVigencia);
    if (fechaVigencia < new Date()) {
      return {
        title: "Solicitud vencida",
        message: `Esta solicitud venció el ${fechaVigencia.toLocaleDateString(
          "es-ES",
          {
            year: "numeric",
            month: "long",
            day: "numeric",
          }
        )}.`,
      };
    }

    if (hasEntryThisMonth()) {
      return {
        title: "Registro mensual completado",
        message: `Ya se ha registrado una factura para este mes.
                  La próxima facturación estará disponible a partir del ${getNextAvailableDate()}.`,
      };
    }

    return null;
  };

  const FacturacionTable = ({ solicitud, onEdit }) => {
    const [isPdf, setIsPdf] = useState(false);
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [currentFile, setCurrentFile] = useState({
      url: "",
      name: "",
      type: "",
    });
    const [modalMedicamentosOpen, setModalMedicamentosOpen] = useState(false);
    const [medicamentosActuales, setMedicamentosActuales] = useState([]);
    const [facturaIndexActual, setFacturaIndexActual] = useState(null);

    // Función para determinar si un archivo es PDF
    const checkIfPdf = (url) => {
      return (
        url.toLowerCase().endsWith(".pdf") ||
        url.toLowerCase().includes("/pdf/") ||
        url.toLowerCase().includes("content-type=application%2Fpdf")
      );
    };

    // Función para abrir modal de medicamentos
    const handleOpenMedicamentos = (factura, index) => {
      setMedicamentosActuales(factura.medicamentos || []);
      setFacturaIndexActual(index);
      setModalMedicamentosOpen(true);
    };

    // Función para guardar medicamentos editados
    const handleSaveMedicamentos = async (medicamentosEditados) => {
      if (facturaIndexActual === null) return;

      try {
        // Actualizar el array de facturación con los nuevos medicamentos
        const facturacionActualizada = [...solicitud.facturacion];
        facturacionActualizada[facturaIndexActual] = {
          ...facturacionActualizada[facturaIndexActual],
          medicamentos: medicamentosEditados,
        };

        // Llamar al endpoint para actualizar
        await dispatch(
          addFacturacion({
            identificador: String(solicitud.identificador),
            facturacionArray: facturacionActualizada,
          })
        );

        toast.success("Medicamentos actualizados exitosamente");
      } catch (error) {
        console.error("Error al actualizar medicamentos:", error);
        toast.error("Error al actualizar medicamentos");
      }
    };

    // Función para abrir archivo en modal
    const openFileViewer = (fileUrl, fileName) => {
      if (!fileUrl) {
        toast.error("No hay archivo disponible");
        return;
      }

      if (fileUrl.startsWith("http")) {
        // Usar URL prefirmada para archivos en DO Spaces
        getUrlPrefirmada(fileUrl)
          .then((response) => {
            if (response.data && response.data.url) {
              const prefirmedUrl = response.data.url;
              const isPdfFile = checkIfPdf(fileUrl);
              const fileType = isPdfFile ? "pdf" : "image";

              setCurrentFile({
                url: prefirmedUrl,
                name: fileName || (isPdfFile ? "Factura PDF" : "Factura"),
                type: fileType,
              });
              setFileViewerOpen(true);
            } else {
              console.error("Facturacion - Error en respuesta:", response);
              toast.error("No se pudo obtener la URL prefirmada");
            }
          })
          .catch((error) => {
            console.error(
              "Facturacion - Error al obtener URL prefirmada:",
              error
            );
            toast.error("Error al obtener la URL prefirmada");
          });
      } else {
        // Si es base64 o datos locales, usar directamente
        const isPdfFile = checkIfPdf(fileUrl);
        const fileType = isPdfFile ? "pdf" : "image";

        setCurrentFile({
          url: fileUrl,
          name: fileName || (isPdfFile ? "Factura PDF" : "Factura"),
          type: fileType,
        });
        setFileViewerOpen(true);
      }
    };

    if (
      !solicitud ||
      !solicitud.facturacion ||
      solicitud.facturacion.length === 0
    ) {
      return (
        <div className="text-center p-4 bg-light rounded">
          <i className="ri-file-list-3-line text-muted font-size-24 mb-2"></i>
          <p className="text-muted mb-3">
            No hay información de facturación disponible.
          </p>
          {tipoEfectivo(solicitud) === "Aguda" && 
           getMontoCobertura(solicitud.montoCobertura) > 0 && 
           solicitud?.verificacionDocumentos?.verificado === true && (
            <Button
              color="primary"
              className="btn-sm"
              onClick={() => {
                setShowFacturacion(false);
                setEstadoFacturacion("idle");
              }}
            >
              <i className="ri-add-line me-1"></i> Agregar Factura
            </Button>
          )}
          {solicitud?.estatus === "Nueva Solicitud" &&
            tipoEfectivo(solicitud) === "Cronica" &&
            isValidSolicitud() &&
            hasDeliveryThisMonth() &&
            !hasInvoiceThisMonth() && (
              <Button
                color="primary"
                className="btn-sm"
                onClick={() => setShowFacturacion(false)}
              >
                <i className="ri-add-line me-1"></i> Agregar Factura
              </Button>
            )}
        </div>
      );
    }

    return (
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Historial de Facturación</h5>

            {solicitud?.estatus === "Nueva Solicitud" &&
              tipoEfectivo(solicitud) === "Cronica" &&
              isValidSolicitud() &&
              hasDeliveryThisMonth() &&
              !hasInvoiceThisMonth() && (
                <Button
                  color="primary"
                  size="sm"
                  className="d-flex align-items-center"
                  onClick={() => {
                    setIsEditing(false);
                    setShowFacturacion(false);
                  }}
                >
                  <i className="ri-add-line me-1"></i> Agregar Nueva Factura
                </Button>
              )}
          </div>

          <div className="table-responsive">
            <table className="table table-centered table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col"># Factura</th>
                  {solicitud?.aseguradora === "Seguros Mercantil" && (
                    <th scope="col">SAINT</th>
                  )}
                  <th scope="col">Total (Bs.)</th>
                  <th scope="col">Total ($)</th>
                  <th scope="col" className="text-center">Medicamentos</th>
                  <th scope="col">Estatus Pago</th>
                  <th scope="col">Factura</th>
                  <th scope="col" className="text-center">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody>
                {solicitud.facturacion.map((factura, index) => {
                  // Definir isPdf aquí para cada factura
                  const isPdf =
                    factura.adjunto &&
                    (factura.adjunto.toLowerCase().endsWith(".pdf") ||
                      factura.adjunto.toLowerCase().includes("/pdf/") ||
                      factura.adjunto
                        .toLowerCase()
                        .includes("content-type=application%2Fpdf"));

                  return (
                    <tr key={index}>
                      <td>
                        {factura.fecha ? factura.fecha.split("T")[0] : "N/A"}
                      </td>
                      <td>
                        <span className="fw-medium font-monospace">
                          {factura.numeroFactura || "N/A"}
                        </span>
                      </td>
                      {solicitud?.aseguradora === "Seguros Mercantil" && (
                        <td>
                          <span className="font-monospace">
                            {factura.saint || "-"}
                          </span>
                        </td>
                      )}
                      <td>
                        <span className="text-primary fw-medium">
                          {factura.montoTotal
                            ? new Intl.NumberFormat("es-VE", {
                                style: "decimal",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true,
                              }).format(parseFloat(factura.montoTotal))
                            : "N/A"}
                        </span>
                      </td>
                      <td>
                        <span className="text-primary fw-medium">
                          {factura.montoDolar
                            ? new Intl.NumberFormat("es-VE", {
                                style: "decimal",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true,
                              }).format(parseFloat(factura.montoDolar))
                            : "N/A"}
                        </span>
                      </td>
                      <td className="text-center">
                        {factura.medicamentos && factura.medicamentos.length > 0 ? (
                          <Button
                            color="link"
                            className="p-0 text-info d-flex align-items-center justify-content-center"
                            onClick={() => handleOpenMedicamentos(factura, index)}
                          >
                            {factura.medicamentos.length}
                            {factura.medicamentos.some(m => m.extraido_con_ia) && (
                              <Badge color="info" className="ms-2 badge-soft">
                                <i className="ri-robot-line"></i>
                              </Badge>
                            )}
                          </Button>
                        ) : (
                          <Button
                            color="link"
                            className="p-0 text-muted"
                            onClick={() => handleOpenMedicamentos(factura, index)}
                          >
                            <i className="ri-add-line"></i>
                          </Button>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <EstatusPagoBadge 
                            estatusPago={factura.trackingPago?.estatusPago} 
                          />
                          {factura.trackingPago && (
                            <TrackingPagoTooltip 
                              trackingPago={factura.trackingPago}
                              facturaId={factura.id || index}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        {factura.adjunto ? (
                          <Button
                            color="link"
                            className="p-0 text-info"
                            onClick={(e) => {
                              e.preventDefault();
                              if (factura.adjunto) {
                                // Usar el modal FileViewer en lugar de window.open
                                const isPdf = checkIfPdf(factura.adjunto);
                                setIsPdf(isPdf);
                                openFileViewer(
                                  factura.adjunto,
                                  isPdf ? "Factura PDF" : "Factura"
                                );
                              }
                            }}
                          >
                            <i className="ri-eye-line me-1"></i>
                            Ver
                          </Button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {(() => {
                          // Función para verificar si han pasado más de un mes
                          const isOlderThanOneMonth = (dateString) => {
                            if (!dateString) return false;
                            const updatedDate = new Date(dateString);
                            const currentDate = new Date();
                            const oneMonthAgo = new Date();
                            oneMonthAgo.setMonth(currentDate.getMonth() - 1);
                            return updatedDate < oneMonthAgo;
                          };

                          // Verificar condiciones para deshabilitar edición
                          const shouldDisableEdit = 
                            solicitud.estatus === "Cancelado" ||
                            (solicitud.estatus === "Entregado" && isOlderThanOneMonth(solicitud.actualizado));

                          return shouldDisableEdit ? (
                            <span className="text-muted">-</span>
                          ) : (
                            <Button
                              color="soft-primary"
                              size="sm"
                              className="btn-icon rounded-circle"
                              onClick={() => onEdit(factura)}
                            >
                              <i className="ri-pencil-fill"></i>
                            </Button>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal para visualizar archivos */}
        <FileViewer
          isOpen={fileViewerOpen}
          toggle={() => setFileViewerOpen(!fileViewerOpen)}
          fileUrl={currentFile.url}
          fileName={currentFile.name}
          fileType={currentFile.type}
        />

        {/* Modal para medicamentos */}
        <ModalMedicamentosFactura
          isOpen={modalMedicamentosOpen}
          toggle={() => setModalMedicamentosOpen(false)}
          medicamentos={medicamentosActuales}
          onSave={handleSaveMedicamentos}
          facturaNumero={
            facturaIndexActual !== null
              ? solicitud.facturacion[facturaIndexActual]?.numeroFactura
              : ""
          }
          readOnly={solicitud.estatus === "Cancelado"}
        />
      </div>
    );
  };

  //principal
  return (
    <React.Fragment>
      {!showFacturacion ? (
        <Card className="border-0 shadow-sm" ref={facturacionSectionRef}>
          <CardHeader>
            <div className="d-flex align-items-center">
              <i className="ri-information-line me-1 text-primary me-2 fs-20"></i>
              <h5 className="card-title mb-0">
                {isEditing ? "Editar Facturación" : "Nueva Facturación"}
              </h5>
            </div>
          </CardHeader>

          <CardBody className="p-4">
            <Form 
              id="facturacion-form" 
              className="form-steps"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              {tipoEfectivo(solicitud) === "Aguda" && solicitud.verificacionDocumentos?.verificado !== true && (
                <div
                  className="alert alert-danger d-flex align-items-start mb-3"
                  role="alert"
                >
                  <i className="ri-error-warning-line me-2 fs-18 mt-1"></i>
                  <div>
                    <strong>🔒 Verificación de Documentos requerida</strong>
                    <p className="mb-0 mt-1">
                      No se puede registrar facturación sin verificar los documentos adjuntos. 
                      Por favor, verifique los documentos primero en la sección de "Adjuntos".
                    </p>
                  </div>
                </div>
              )}

              {tipoEfectivo(solicitud) === "Aguda" && 
               solicitud.verificacionDocumentos?.verificado === true && 
               (!solicitud.porcentaje_avance || solicitud.porcentaje_avance < 20) && (
                <div
                  className="alert alert-warning d-flex align-items-start mb-3"
                  role="alert"
                >
                  <i className="ri-alert-line me-2 fs-18 mt-1"></i>
                  <div>
                    <strong> Diagnóstico Farmacológico requerido</strong>
                    <p className="mb-0 mt-1">
                      No se puede registrar facturación sin diagnóstico farmacológico. 
                      Por favor, registre el diagnóstico primero en la sección de "Diagnóstico Farmacológico".
                    </p>
                  </div>
                </div>
              )}

              {tipoEfectivo(solicitud) === "Cronica" && (() => {
                const tieneMedicamentosEsteMes = () => {
                  if (!solicitud.cronico?.historialEntregas?.length) return false;
                  
                  const today = new Date();
                  const currentMonth = today.getMonth();
                  const currentYear = today.getFullYear();
                  
                  return solicitud.cronico.historialEntregas.some((entrega) => {
                    const entregaDate = new Date(entrega.fecha);
                    return (
                      entregaDate.getMonth() === currentMonth &&
                      entregaDate.getFullYear() === currentYear &&
                      entrega.medicamentos?.length > 0
                    );
                  });
                };

                return !tieneMedicamentosEsteMes() && (
                  <div
                    className="alert alert-warning d-flex align-items-start mb-3"
                    role="alert"
                  >
                    <i className="ri-alert-line me-2 fs-18 mt-1"></i>
                    <div>
                      <strong> Medicamentos requeridos</strong>
                      <p className="mb-0 mt-1">
                        No se puede registrar facturación sin medicamentos registrados este mes. 
                        Por favor, registre los medicamentos primero en la sección de "Registro de Medicamentos".
                      </p>
                    </div>
                  </div>
                );
              })()}
              
              {/* Información de Cobertura - Mostrar SIEMPRE */}
              <Card className="mb-4" style={{ backgroundColor: '#f0f9ff', border: '1px solid #e0f2fe' }}>
                <CardBody className="py-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <div className="avatar-xs">
                        <span className="avatar-title rounded-circle" style={{ backgroundColor: '#3b82f6' }}>
                          <i className="ri-money-dollar-circle-line text-white"></i>
                        </span>
                      </div>
                      <div>
                        <p className="text-muted mb-0 small">Monto de Cobertura</p>
                        <h5 className="mb-0" style={{ color: '#1e40af' }}>
                          {monto > 0 ? (
                            <>
                              {/* Para Seguros Mercantil: mostrar en Bs, para MyH+: mostrar en USD */}
                              {esMercantilSolicitud ? (
                                <>
                                  {new Intl.NumberFormat("es-VE", {
                                    style: "decimal",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true,
                                  }).format(parseFloat(monto))}{" "}
                                  <span className="fs-14">Bs</span>
                                </>
                              ) : (
                                <>
                                  {new Intl.NumberFormat("es-VE", {
                                    style: "decimal",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true,
                                  }).format(parseFloat(monto))}{" "}
                                  <span className="fs-14">USD</span>
                                </>
                              )}
                            </>
                          ) : (
                            <span className="text-warning">Sin cobertura asignada</span>
                          )}
                        </h5>
                      </div>
                    </div>
                    {monto > 0 && tasaDolar && (
                      <div className="text-end">
                        <p className="text-muted mb-0 small">
                          Tasa: {tasaDolar.precio.toFixed(2)} Bs/USD{" "}
                          <span className="badge bg-soft-success text-success">{tasaDolar.ultima_actualizacion}</span>
                        </p>
                        <h5 className="mb-0" style={{ color: '#059669' }}>
                          {/* Mostrar equivalente en la moneda opuesta */}
                          {esMercantilSolicitud ? (
                            <>
                              {/* Mercantil: Bs → USD */}
                              {new Intl.NumberFormat("es-VE", {
                                style: "decimal",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true,
                              }).format(parseFloat(monto) / tasaDolar.precio)}{" "}
                              <span className="fs-14">USD</span>
                            </>
                          ) : (
                            <>
                              {/* MyH+: USD → Bs */}
                              {new Intl.NumberFormat("es-VE", {
                                style: "decimal",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true,
                              }).format(parseFloat(monto) * tasaDolar.precio)}{" "}
                              <span className="fs-14">Bs</span>
                            </>
                          )}
                        </h5>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {getStatusMessage() && (
                <div
                  className={`alert ${
                    getStatusMessage().type === "warning" 
                      ? "alert-warning" 
                      : !isValidSolicitud() 
                        ? "alert-warning" 
                        : "alert-info"
                  } d-flex align-items-center`}
                  role="alert"
                >
                  <i
                    className={`me-2 fs-18 ${
                      getStatusMessage().type === "warning" || !isValidSolicitud()
                        ? "ri-error-warning-line"
                        : "ri-information-line"
                    }`}
                  ></i>
                  <div>
                    <h6 className="alert-heading mb-1">
                      {getStatusMessage().title}
                    </h6>
                    <p className="mb-0 small">{getStatusMessage().message}</p>
                  </div>
                </div>
              )}

              {((tipoEfectivo(solicitud) === "Aguda" && 
                 getMontoCobertura(solicitud.montoCobertura) > 0 && 
                 solicitud.verificacionDocumentos?.verificado === true) ||
                (solicitud.estatus === "Nueva Solicitud" &&
                  tipoEfectivo(solicitud) === "Cronica" &&
                  isValidSolicitud() &&
                  hasDeliveryThisMonth() &&
                  !hasInvoiceThisMonth()) ||
                isEditing) && (
                <>
                  {/* Formulario de facturación habilitado */}
                  {/* Flujo unificado: Upload automático con IA, sin tabs */}
                  {estadoFacturacion === "idle" && (
                    <>
                      <ValidadorImagenFactura 
                        onValidacionBackend={handleValidacionIA}
                      />
                      
                      {/* Opción para ingresar manualmente */}
                      <div className="text-center mt-4">
                        <div className="d-flex align-items-center justify-content-center mb-3">
                          <div className="vr"></div>
                          <span className="px-3 text-muted small">O</span>
                          <div className="vr"></div>
                        </div>
                        <Button
                          color="link"
                          className="text-muted"
                          onClick={() => {
                            setEstadoFacturacion("manual");
                            setModoManualExplicito(true); // Marcar como modo manual explícito
                          }}
                        >
                          <i className="ri-edit-line me-1"></i>
                          Ingresar datos manualmente
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Mostrar loader mientras se procesa */}
                  {estadoFacturacion === "procesando" && (
                    <Card className="mb-4">
                      <CardBody className="text-center py-5">
                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                          <span className="visually-hidden">Procesando...</span>
                        </div>
                        <h5 className="text-primary mb-2">
                          <i className="ri-robot-line me-2"></i>
                          Procesando factura con IA
                        </h5>
                        <p className="text-muted mb-0">
                          Extrayendo datos de la factura... Por favor espera.
                        </p>
                      </CardBody>
                    </Card>
                  )}

                  {/* Mostrar formulario cuando hay datos (manual o extraídos) o cuando hay error */}
                  {(estadoFacturacion === "success" || estadoFacturacion === "manual" || estadoFacturacion === "error") && (
                    <>
                      {/* Preview de imagen extraída con IA - Mostrar tanto en creación como en edición */}
                      {imagenFacturaPreview && (
                        <Card className="mb-4">
                          <CardHeader className={estadoFacturacion === "error" ? "bg-danger-subtle" : "bg-success-subtle"}>
                            <h6 className={`mb-0 ${estadoFacturacion === "error" ? "text-danger" : "text-success"}`}>
                              <i className={`${estadoFacturacion === "error" ? "ri-error-warning-line" : "ri-file-check-line"} me-2`}></i>
                              {estadoFacturacion === "error" ? 'Imagen con Problemas de Calidad' : (isEditing ? 'Nueva Factura Procesada con IA' : 'Factura Procesada con IA')}
                            </h6>
                          </CardHeader>
                          <CardBody>
                            <div className="text-center">
                              {(() => {
                                const isPDF = imagenFacturaPreview instanceof File 
                                  ? imagenFacturaPreview.type === 'application/pdf'
                                  : (typeof imagenFacturaPreview === 'string' && imagenFacturaPreview.includes('application/pdf'));
                                
                                if (isPDF) {
                                  // Mostrar ícono de PDF
                                  return (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                      <i className="ri-file-pdf-line text-danger" style={{ fontSize: '80px' }}></i>
                                      <div className="mt-3 text-muted">
                                        <strong>Archivo PDF procesado</strong>
                                      </div>
                                      <div className="text-muted small">
                                        Los datos han sido extraídos correctamente
                                      </div>
                                    </div>
                                  );
                                } else {
                                  // Mostrar imagen
                                  return (
                                    <img 
                                      src={imagenFacturaPreview instanceof File 
                                        ? URL.createObjectURL(imagenFacturaPreview)
                                        : imagenFacturaPreview
                                      }
                                      alt="Preview de factura"
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  );
                                }
                              })()}
                            </div>
                            
                            {/* Mensaje según el estado */}
                            {estadoFacturacion === "error" && errorImagenDetalle ? (
                              <div className="mt-3">
                                <div className="alert alert-danger mb-0">
                                  <h6 className="alert-heading mb-2">
                                    <i className="ri-error-warning-line me-1"></i>
                                    Imagen de Baja Calidad Detectada
                                  </h6>
                                  <p className="mb-2">
                                    La imagen que subiste es muy pequeña (<strong>{errorImagenDetalle.dimensiones}</strong>, <strong>{errorImagenDetalle.tamaño}</strong>, <strong>{errorImagenDetalle.megapixels}</strong>) y no se puede procesar con precisión.
                                  </p>
                                  <div className="bg-light p-3 rounded mb-3">
                                    <p className="mb-2 fw-semibold text-success">
                                      <i className="ri-lightbulb-line me-1"></i>
                                      Soluciones:
                                    </p>
                                    <ul className="mb-0 ps-3">
                                      <li>Toma una nueva foto con la cámara en <strong>CALIDAD MÁXIMA</strong></li>
                                      <li>Asegúrate de <strong>NO enviar</strong> la foto por WhatsApp/Telegram (la comprimen)</li>
                                      <li>Verifica que el archivo tenga más de <strong>500KB</strong></li>
                                      <li>La imagen debe tener al menos <strong>800x600px (0.48MP)</strong></li>
                                    </ul>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => limpiarEstadosError("idle")}
                                    >
                                      <i className="ri-upload-2-line me-1"></i>
                                      Subir Otra Imagen
                                    </Button>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={() => {
                                        limpiarEstadosError("manual");
                                        setMedicamentosExtraidos([]); // Limpiar medicamentos al cambiar a modo manual
                                      }}
                                    >
                                      <i className="ri-edit-line me-1"></i>
                                      Ingresar Datos Manualmente
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      onClick={() => {
                                        if (isEditing) {
                                          // En modo edición: volver a la tabla de facturas
                                          limpiarEstadosError("idle");
                                          setIsEditing(false);
                                          setEditingFacturaIndex(null);
                                          setShowFacturacion(false);
                                        } else {
                                          // En modo creación: solo limpiar error
                                          limpiarEstadosError("idle");
                                        }
                                      }}
                                    >
                                      <i className="ri-close-line me-1"></i>
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 text-center text-muted small">
                                <i className="ri-information-line me-1"></i>
                                Datos extraídos automáticamente. Revisa y confirma antes de guardar.
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      )}
                      
                      {/* Solo mostrar formulario si NO hay error de imagen */}
                      {estadoFacturacion !== "error" && (
                      <div className="bg-light p-4 rounded mb-4">
                        <Row className="g-3">
                          <Col xs={12} sm={6} lg={3}>
                            <Label
                              className="form-label fw-medium"
                              htmlFor="FechaFacturacion"
                            >
                              Fecha <span className="text-danger">*</span>
                            </Label>
                            <div className="position-relative">
                              <Input
                                type="date"
                                className={`form-control ${
                                  formErrors.fechaFacturacion || fechaEntregaError || alertaFecha ? "is-invalid" : ""
                                } ${alertaFecha ? "border-warning" : ""}`}
                                id="FechaFacturacion"
                                value={fechaFacturacion || ""}
                                onChange={handleFechaFacturacion}
                                max={new Date().toISOString().split('T')[0]}
                                lang="es-VE"
                                required
                                disabled={isEditing}
                              />
                              
                              {/* Botones de acción cuando hay alerta de fecha */}
                              {alertaFecha && (
                                <div className="position-absolute" style={{ right: "40px", top: "50%", transform: "translateY(-50%)", display: "flex", gap: "4px" }}>
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="btn-icon rounded-circle"
                                    onClick={confirmarFechaFueraMes}
                                    id="confirmarFechaTooltip"
                                    style={{ padding: "4px 8px", fontSize: "12px" }}
                                  >
                                    <i className="ri-check-line"></i>
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    className="btn-icon rounded-circle"
                                    onClick={limpiarFecha}
                                    id="limpiarFechaTooltip"
                                    style={{ padding: "4px 8px", fontSize: "12px" }}
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                </div>
                              )}
                              
                              {/* Tooltips fuera del condicional para evitar errores de montaje */}
                              {alertaFecha && (
                                <>
                                  <Tooltip
                                    placement="top"
                                    isOpen={tooltipOpen.confirmarFechaTooltip}
                                    target="confirmarFechaTooltip"
                                    toggle={() => toggleTooltip('confirmarFechaTooltip')}
                                  >
                                    Confirmar fecha
                                  </Tooltip>
                                  <Tooltip
                                    placement="top"
                                    isOpen={tooltipOpen.limpiarFechaTooltip}
                                    target="limpiarFechaTooltip"
                                    toggle={() => toggleTooltip('limpiarFechaTooltip')}
                                  >
                                    Limpiar fecha
                                  </Tooltip>
                                </>
                              )}
                            </div>
                            
                            {/* Alerta de fecha fuera del mes-año actual */}
                            {alertaFecha && (
                              <div className="alert alert-warning d-flex align-items-center mt-2 mb-0" role="alert">
                                <i className="ri-alert-line me-2"></i>
                                <div className="flex-grow-1">
                                  <small className="mb-0">{alertaFecha.mensaje}</small>
                                </div>
                              </div>
                            )}
                            
                            {formErrors.fechaFacturacion && (
                              <div className="invalid-feedback d-block">
                                <i className="ri-error-warning-line me-1"></i>
                                {typeof formErrors.fechaFacturacion === 'string' 
                                  ? formErrors.fechaFacturacion 
                                  : "Este campo es requerido"}
                              </div>
                            )}
                            {fechaEntregaError && !formErrors.fechaFacturacion && (
                              <div className="invalid-feedback d-block">
                                <i className="ri-error-warning-line me-1"></i>
                                {fechaEntregaError}
                              </div>
                            )}
                            
                            {/* Mensaje informativo cuando se está editando */}
                            {isEditing && (
                              <div className="form-text text-muted mt-1">
                                <i className="ri-information-line me-1"></i>
                                La fecha de factura no puede modificarse. Si necesita cambiar la fecha, debe eliminar esta factura y crear una nueva.
                              </div>
                            )}
                          </Col>

                          <Col xs={12} sm={6} lg={3}>
                            <Label className="form-label fw-medium" htmlFor="factura">
                              # Factura <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="text"
                              className={`form-control ${
                                formErrors.numeroFactura ? "is-invalid" : ""
                              } ${
                                validacionFactura.estado === "unica"
                                  ? "is-valid"
                                  : ""
                              } ${
                                validacionFactura.estado === "duplicada"
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="factura"
                              value={numeroFactura || ""}
                              onChange={handleNumeroFactura}
                              onBlur={validarNumeroFactura}
                              disabled={validacionFactura.estado === "validando"}
                              required
                            />
                            {formErrors.numeroFactura && (
                              <div className="invalid-feedback">
                                Este campo es requerido
                              </div>
                            )}
                            {validacionFactura.estado === "duplicada" && (
                              <div className="invalid-feedback d-block">
                                <i className="ri-error-warning-line me-1"></i>
                                {validacionFactura.mensaje}
                              </div>
                            )}
                            {validacionFactura.estado === "validando" && (
                              <div className="form-text">
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                ></span>
                                Validando...
                              </div>
                            )}
                          </Col>

                          {/* Campo SAINT - Solo para Seguros Mercantil */}
                          {solicitud?.aseguradora === "Seguros Mercantil" && (
                            <Col xs={12} sm={6} lg={3}>
                              <Label className="form-label fw-medium" htmlFor="saint">
                                <i className="ri-file-list-line me-1"></i>
                                Número SAINT <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="text"
                                className={`form-control ${
                                  formErrors.saint ? "is-invalid" : ""
                                }`}
                                id="saint"
                                value={saint || ""}
                                onChange={handleSaint}
                                placeholder="Ej: 00125183"
                                disabled={loadingx}
                                required
                              />
                              {formErrors.saint && (
                                <div className="invalid-feedback">
                                  <i className="ri-error-warning-line me-1"></i>
                                  {formErrors.saint}
                                </div>
                              )}
                              <div className="form-text">
                                <i className="ri-information-line me-1"></i>
                                Documento SAINT Nro (preservar ceros a la izquierda)
                              </div>
                            </Col>
                          )}

                          <Col xs={12} sm={6} lg={3}>
                            <Label className="form-label fw-medium" htmlFor="total">
                              Monto Total (Bs) <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                              <span className="input-group-text">Bs</span>
                              <Input
                                type="text"
                                className={`form-control ${
                                  formErrors.montoTotal ? "is-invalid" : ""
                          }`}
                          id="total"
                          value={montoTotal || ""}
                          onChange={(e) => {
                            setErrorMessage("");
                            handleMontoTotal(e);
                          }}
                          onBlur={(e) => {
                            if (montoTotal) {
                              const numberValue = montoTotal.replace(",", ".");
                              if (!isNaN(numberValue)) {
                                const formattedValue = parseFloat(numberValue)
                                  .toFixed(2)
                                  .replace(".", ",");
                                setMontoTotal(formattedValue);
                              }
                            }
                          }}
                          placeholder="0,00"
                          required
                        />
                        {formErrors.montoTotal && (
                          <div className="invalid-feedback">
                            {formErrors.montoTotal}
                          </div>
                        )}
                        {montoExcedido && !formErrors.montoTotal && (
                          <div className="text-warning small mt-1">
                            <i className="ri-error-warning-line me-1"></i>
                            {mensajeExcedente}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col lg={12} className="mb-3">
                      {/* Solo mostrar label "Adjuntar Factura" si no hay datos extraídos por IA */}
                      {!imagenFacturaPreview && !isEditing && (
                        <Label className="form-label fw-medium">
                          Adjuntar Factura <span className="text-danger">*</span>
                        </Label>
                      )}
                      
                      {/* Vista previa de imagen existente en modo edición */}
                      {isEditing && archivoAdjunto && (
                        <Card className="mb-3">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <i className="ri-image-line fs-20 text-primary me-2"></i>
                                <div>
                                  <div className="fw-medium">Imagen actual de la factura</div>
                                  <small className="text-muted">
                                    Haz clic en "Ver" para visualizar o sube una nueva imagen para reemplazarla
                                  </small>
                                </div>
                              </div>
                              <Button
                                color="primary"
                                size="sm"
                                outline
                                onClick={() => {
                                  if (archivoAdjunto.startsWith('http')) {
                                    getUrlPrefirmada(archivoAdjunto)
                                      .then(response => {
                                        if (response.data && response.data.url) {
                                          setFacturaImageUrl(response.data.url);
                                          setModalIsOpen(true);
                                        }
                                      });
                                  } else {
                                    setFacturaImageUrl(createBlobUrl(archivoAdjunto));
                                    setModalIsOpen(true);
                                  }
                                }}
                              >
                                <i className="ri-eye-line me-1"></i>
                                Ver
                              </Button>
                            </div>
                          </CardBody>
                        </Card>
                      )}
                      
                      {/* FilePond para subir nueva imagen */}
                      {!imagenFacturaPreview && (
                        <div
                          className={
                            formErrors.files ? "border border-danger rounded" : ""
                          }
                        >
                          <FilePond
                          files={files}
                          onupdatefiles={async (fileItems) => {
                            setFiles(fileItems);
                            
                            // Si se sube una nueva imagen en modo edición Y NO estamos en modo manual explícito, activar flujo de IA
                            if (isEditing && !modoManualExplicito && fileItems.length > 0 && !processingFileRef.current) {
                              //console.log('🔄 Nueva imagen subida en modo edición, activando flujo de IA');
                              
                              // Marcar como procesando para evitar ciclo infinito
                              processingFileRef.current = true;
                              
                              const fileItem = fileItems[0];
                              
                              // Cambiar estado a "procesando"
                              setEstadoFacturacion("procesando");
                              
                              try {
                                // Obtener el archivo original
                                let file = fileItem.file;
                                
                                // En modo edición, fileItem.file puede no ser una instancia de File
                                // Intentar obtener el archivo real de diferentes maneras
                                if (!file || !(file instanceof File)) {
                                  //console.log('🔄 fileItem.file no es File, intentando obtener de otra forma:', file);
                                  
                                  // Opción 1: Intentar obtener desde fileItem directamente
                                  if (fileItem.source instanceof File) {
                                    file = fileItem.source;
                                    //console.log('✅ Archivo obtenido desde fileItem.source');
                                  }
                                  // Opción 2: Crear un nuevo File desde el objeto
                                  else if (file && file.name && file.type) {
                                    // Si tenemos un blob o datos, intentar crear un File
                                    try {
                                      const response = await fetch(fileItem.getFileEncodeDataURL());
                                      const blob = await response.blob();
                                      file = new File([blob], file.name, { type: file.type });
                                      //console.log('✅ Archivo recreado desde DataURL');
                                    } catch (e) {
                                      console.error('❌ No se pudo recrear el archivo:', e);
                                      throw new Error('No se pudo obtener el archivo. Por favor, intenta subirlo nuevamente.');
                                    }
                                  } else {
                                    console.error('❌ No se pudo obtener el archivo de ninguna forma');
                                    throw new Error('No se pudo obtener el archivo. Por favor, intenta subirlo nuevamente.');
                                  }
                                }
                                
                                // Validar que ahora sí tenemos un File válido
                                if (!(file instanceof File)) {
                                  console.error('❌ Después de todos los intentos, file no es un File válido:', file);
                                  throw new Error('No se pudo obtener el archivo. Por favor, intenta subirlo nuevamente.');
                                }
                                
                                // DIAGNÓSTICO: Leer dimensiones de la imagen
                                const img = new Image();
                                const imageLoadPromise = new Promise((resolve, reject) => {
                                  img.onload = () => {
                                    /*console.log('🖼️ DIAGNÓSTICO - Dimensiones de imagen:', {
                                      width: img.width,
                                      height: img.height,
                                      megapixels: (img.width * img.height / 1000000).toFixed(2) + 'MP',
                                      fileSize: (file.size / 1024).toFixed(2) + 'KB',
                                      fileName: file.name,
                                      fileType: file.type
                                    });*/
                                    resolve();
                                  };
                                  img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
                                });
                                img.src = URL.createObjectURL(file);
                                await imageLoadPromise;
                                
                                // Obtener token de autenticación
                                const token = SecureStorage.getAccessToken();
                                
                                /*console.log('📤 Enviando imagen a IA:', {
                                  tipo_solicitud: solicitud.tipo,
                                  filename: file.name,
                                  size: file.size,
                                  type: file.type
                                });*/
                                
                                // Crear FormData para enviar el archivo
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                // Llamar al endpoint de extracción
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/facturas-ia/extraer-datos`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                    // NO incluir Content-Type, el navegador lo establece automáticamente con boundary
                                  },
                                  body: formData
                                });
                                
                                const data = await response.json();
                                
                                // Si hay error, mostrar detalle completo
                                if (!response.ok) {
                                  console.error('❌ Error del servidor:', {
                                    status: response.status,
                                    detail: data.detail,
                                    message: data.message || data.error
                                  });
                                  
                                  // Formatear mensaje de error - priorizar el mensaje específico del backend
                                  let errorMsg = 'Error al procesar la imagen con IA.';
                                  
                                  // 1. Intentar obtener mensaje específico del backend
                                  if (data.message) {
                                    errorMsg = data.message;
                                  } else if (data.error) {
                                    errorMsg = data.error;
                                  } 
                                  // 2. Si viene en detail como array (validación de Pydantic)
                                  else if (Array.isArray(data.detail)) {
                                    errorMsg = data.detail.map(d => d.msg || d).join(', ');
                                  } 
                                  // 3. Si viene en detail como string
                                  else if (typeof data.detail === 'string') {
                                    errorMsg = data.detail;
                                  }
                                  
                                  // Detectar si es error de imagen pequeña
                                  if (errorMsg.toLowerCase().includes('imagen pequeña') || errorMsg.toLowerCase().includes('imagen muy pequeña')) {
                                    // Guardar detalles del error para mostrar debajo de la vista previa
                                    setErrorImagenDetalle({
                                      tipo: 'imagen_pequeña',
                                      dimensiones: `${img.width}x${img.height}px`,
                                      tamaño: `${(file.size/1024).toFixed(0)}KB`,
                                      megapixels: `${(img.width * img.height / 1000000).toFixed(2)}MP`,
                                      mensajeBackend: errorMsg
                                    });
                                    
                                    // Convertir archivo a base64 para mostrar preview
                                    const base64 = await new Promise((resolve, reject) => {
                                      const reader = new FileReader();
                                      reader.onload = () => resolve(reader.result);
                                      reader.onerror = reject;
                                      reader.readAsDataURL(file);
                                    });
                                    
                                    // Guardar preview para que el usuario vea la imagen que subió
                                    setImagenFacturaPreview(base64);
                                    setImagenFacturaBase64(null); // No guardar para envío
                                    
                                    // Cambiar a estado error para mostrar mensaje debajo de preview
                                    setEstadoFacturacion('error');
                                    
                                    // Toast corto y simple
                                    toast.error(' Imagen de baja calidad. Ver detalles debajo.', { autoClose: 4000 });
                                    return; // Salir sin lanzar excepción
                                  }
                                  
                                  throw new Error(errorMsg);
                                }
                                
                                if (data.success) {
                                  // Convertir archivo a base64 para guardar
                                  const base64 = await new Promise((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = () => resolve(reader.result);
                                    reader.onerror = reject;
                                    reader.readAsDataURL(file);
                                  });
                                  
                                  // Guardar base64 para usar al actualizar
                                  setImagenFacturaBase64(base64);
                                  
                                  // Procesar resultado con handleValidacionIA
                                  handleValidacionIA({
                                    confirmado: true,
                                    success: true,
                                    datos: data.datos,
                                    imagenParaSubir: base64
                                  });
                                  
                                  toast.success('✅ Datos extraídos correctamente. Revisa y confirma.');
                                }
                              } catch (error) {
                                console.error('❌ Error al extraer datos:', error);
                                const errorMessage = error.message || 'Error al procesar la imagen con IA. Completa los datos manualmente.';
                                toast.error(errorMessage, { autoClose: 5000 });
                                limpiarEstadosError("manual");
                              } finally {
                                // Resetear bandera después de un delay para permitir nuevas subidas
                                setTimeout(() => {
                                  processingFileRef.current = false;
                                }, 2000);
                              }
                            }
                          }}
                          allowMultiple={false}
                          maxFiles={1}
                          name="files"
                          labelIdle='<i class="ri-upload-cloud-line fs-24 mb-2"></i><br/>Arrastra o <span class="filepond--label-action text-primary">selecciona</span> un archivo'
                          className="filepond filepond-input-multiple"
                          allowFileEncode={true}
                          acceptedFileTypes={["image/*", "application/pdf"]}
                          labelFileTypeNotAllowed="Solo se permiten imágenes y PDF"
                          fileValidateTypeLabelExpectedTypes="Seleccione una imagen (JPG, PNG, WEBP) o PDF"
                          maxFileSize="10MB"
                          credits={false}
                          allowImageTransform={false}
                          allowImageResize={false}
                          stylePanelLayout="compact"
                          styleLoadIndicatorPosition="center bottom"
                          styleProgressIndicatorPosition="right bottom"
                          styleButtonRemoveItemPosition="left bottom"
                          styleButtonProcessItemPosition="right bottom"
                          allowBrowse={true}
                          allowCamera={true}
                          fileValidateTypeDetectType={(source, type) =>
                            new Promise((resolve, reject) => {
                              // Permitir imágenes y PDFs
                              if (source instanceof File) {
                                if (source.type.includes("image/") || source.type.includes("application/pdf")) {
                                  resolve(source.type);
                                } else {
                                  reject(type);
                                }
                              } else {
                                // Para otros casos, validar si es imagen o PDF
                                if (type && (type.includes("image/") || type.includes("application/pdf"))) {
                                  resolve(type);
                                } else {
                                  reject(type);
                                }
                              }
                            })
                          }
                          onaddfile={(error, file) => {
                            if (error) {
                              console.error("Error al agregar archivo:", error);
                              setFormErrors({
                                ...formErrors,
                                files: "Error al procesar el archivo",
                              });
                              return;
                            }
                            setFormErrors({
                              ...formErrors,
                              files: null,
                            });
                          }}
                          onerror={(error) => {
                            console.error("Error FilePond:", error);
                            setFormErrors({
                              ...formErrors,
                              files: "Error al procesar el archivo",
                            });
                          }}
                        />
                        </div>
                      )}
                      
                      {formErrors.files && (
                        <div className="text-danger small mt-1">
                          <i className="ri-error-warning-line me-1"></i>
                          Debe adjuntar una factura
                        </div>
                      )}
                      {/* Solo mostrar mensaje de formatos si no hay datos extraídos por IA */}
                      {!imagenFacturaPreview && !isEditing && (
                        <div className="text-muted small mt-1">
                          <i className="ri-information-line me-1"></i>
                          Formatos aceptados: Imágenes (JPG, PNG, WEBP) o PDF. Tamaño máximo: 2MB
                        </div>
                      )}
                    </Col>
                  </Row>

                  {/* Tabla de medicamentos extraídos con IA - EDITABLE Y COLAPSABLE */}
                  {medicamentosExtraidos && medicamentosExtraidos.length > 0 && (
                    <Row className="mt-4">
                      <Col lg={12}>
                        {/* Header clickeable para colapsar/expandir */}
                        <div 
                          className="d-flex align-items-center justify-content-between p-3 bg-light-subtle border rounded cursor-pointer"
                          onClick={() => setMedicamentosColapsados(!medicamentosColapsados)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="badge bg-success-subtle text-success p-2 me-2">
                              <i className="ri-medicine-bottle-line fs-18"></i>
                            </div>
                            <div>
                              <h6 className="mb-0">
                                Medicamentos Extraídos
                                <Badge color="success" className="ms-2">
                                  {medicamentosExtraidos.length}
                                </Badge>
                              </h6>
                              <span className="text-muted small">
                                {medicamentosColapsados 
                                  ? 'Click para expandir y revisar medicamentos (opcional)' 
                                  : 'Click para colapsar'
                                }
                              </span>
                            </div>
                          </div>
                          <div>
                            <i className={`ri-arrow-${medicamentosColapsados ? 'down' : 'up'}-s-line fs-20 text-muted`}></i>
                          </div>
                        </div>

                        {/* Contenido colapsable */}
                        <Collapse isOpen={!medicamentosColapsados}>
                          <div className="border-start border-end border-bottom rounded-bottom p-3">
                            {/* Botón agregar medicamento */}
                            <div className="d-flex justify-content-end mb-3">
                              <Button
                                color="primary"
                                size="sm"
                                outline
                                onClick={() => {
                                setMedicamentosExtraidos([
                                  ...medicamentosExtraidos,
                                  {
                                    nombre: '',
                                    nombre_completo: '',
                                    principio_activo: '',
                                    dosis: '',
                                    unidad_dosis: 'MG',
                                    forma_farmaceutica: '',
                                    cantidad_unidades: '',
                                    cantidad_comprada: 1,
                                    laboratorio: '',
                                    marca: '',
                                    tipo: '',
                                    precio_unitario: 0,
                                    precio_total: 0,
                                    extraido_con_ia: false
                                  }
                                ]);
                              }}
                            >
                              <i className="ri-add-line me-1"></i>
                              Agregar Medicamento
                            </Button>
                          </div>
                          
                          <div className="table-responsive">
                            <Table className="table table-sm table-bordered mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center" style={{ width: '2%' }}>#</th>
                                  <th style={{ width: '16%' }}>Medicamento *</th>
                                  <th style={{ width: '11%' }}>Principio Activo</th>
                                  <th style={{ width: '15%' }}>Presentación</th>
                                  <th style={{ width: '9%' }}>Laboratorio</th>
                                  <th className="text-center" style={{ width: '7%' }}>Tipo</th>
                                  <th className="text-center" style={{ width: '4%' }}>Cant.</th>
                                  <th className="text-end" style={{ width: '10%' }}>Precio Unit.</th>
                                  <th className="text-end" style={{ width: '10%' }}>Precio Total</th>
                                  <th className="text-center" style={{ width: '6%' }}>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {medicamentosExtraidos.map((med, idx) => {
                                  // Armar nombre_completo automáticamente
                                  const nombreCompleto = [
                                    med.nombre,
                                    med.dosis && med.unidad_dosis ? `${med.dosis}${med.unidad_dosis}` : '',
                                    med.forma_farmaceutica && med.cantidad_unidades ? `X ${med.cantidad_unidades}${med.forma_farmaceutica}` : '',
                                    med.laboratorio ? `(${med.laboratorio})` : ''
                                  ].filter(Boolean).join(' ');

                                  // Actualizar nombre_completo en el objeto
                                  if (med.nombre_completo !== nombreCompleto) {
                                    med.nombre_completo = nombreCompleto;
                                  }

                                  return (
                                    <tr key={idx}>
                                      <td className="text-center">{idx + 1}</td>
                                      <td>
                                        <Input
                                          type="text"
                                          size="sm"
                                          value={med.nombre || ''}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx].nombre = e.target.value;
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                          placeholder="Nombre"
                                        />
                                        {nombreCompleto && (
                                          <small className="text-muted d-block mt-1">
                                            {nombreCompleto}
                                          </small>
                                        )}
                                      </td>
                                      <td>
                                        <Input
                                          type="text"
                                          size="sm"
                                          value={med.principio_activo || ''}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx].principio_activo = e.target.value;
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                          placeholder="Ej: Losartán"
                                        />
                                      </td>
                                      <td>
                                        <div className="d-flex gap-1 mb-1">
                                          <Input
                                            type="text"
                                            size="sm"
                                            value={med.dosis || ''}
                                            onChange={(e) => {
                                              const newMeds = [...medicamentosExtraidos];
                                              newMeds[idx].dosis = e.target.value;
                                              setMedicamentosExtraidos(newMeds);
                                            }}
                                            placeholder="100"
                                            style={{ maxWidth: '55px' }}
                                          />
                                          <Input
                                            type="select"
                                            size="sm"
                                            value={med.unidad_dosis || 'MG'}
                                            onChange={(e) => {
                                              const newMeds = [...medicamentosExtraidos];
                                              newMeds[idx].unidad_dosis = e.target.value;
                                              setMedicamentosExtraidos(newMeds);
                                            }}
                                            style={{ minWidth: '80px' }}
                                          >
                                            <option value="MG">MG</option>
                                            <option value="MCG">MCG</option>
                                            <option value="G">G</option>
                                            <option value="ML">ML</option>
                                            <option value="UI">UI</option>
                                          </Input>
                                        </div>
                                        <div className="d-flex gap-1">
                                          <Input
                                            type="text"
                                            size="sm"
                                            value={med.forma_farmaceutica || ''}
                                            onChange={(e) => {
                                              const newMeds = [...medicamentosExtraidos];
                                              newMeds[idx].forma_farmaceutica = e.target.value;
                                              setMedicamentosExtraidos(newMeds);
                                            }}
                                            placeholder="COMP"
                                            style={{ maxWidth: '55px' }}
                                          />
                                          <span className="align-self-center">X</span>
                                          <Input
                                            type="number"
                                            size="sm"
                                            value={med.cantidad_unidades || ''}
                                            onChange={(e) => {
                                              const newMeds = [...medicamentosExtraidos];
                                              newMeds[idx].cantidad_unidades = e.target.value;
                                              setMedicamentosExtraidos(newMeds);
                                            }}
                                            placeholder="10"
                                            style={{ maxWidth: '45px' }}
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <Input
                                          type="text"
                                          size="sm"
                                          value={med.laboratorio || ''}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx].laboratorio = e.target.value;
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                          placeholder="Laboratorio"
                                        />
                                      </td>
                                      <td>
                                        <Input
                                          type="select"
                                          size="sm"
                                          value={med.tipo || ''}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx].tipo = e.target.value;
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                        >
                                          <option value="">Tipo</option>
                                          <option value="Genérico">Genérico</option>
                                          <option value="De Marca">De Marca</option>
                                        </Input>
                                      </td>
                                      <td>
                                        <Input
                                          type="number"
                                          size="sm"
                                          min="1"
                                          value={med.cantidad_comprada || 1}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx] = { ...newMeds[idx] }; // Clonar objeto
                                            const cant = parseFloat(e.target.value) || 0;
                                            newMeds[idx].cantidad_comprada = cant;
                                            
                                            // Recalcular total
                                            const unitario = parseFloat(newMeds[idx].precio_unitario) || 0;
                                            if (unitario > 0) {
                                              newMeds[idx].precio_total = parseFloat((cant * unitario).toFixed(2));
                                            }
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                        />
                                      </td>
                                      <td>
                                        <Input
                                          type="number"
                                          size="sm"
                                          step="0.01"
                                          min="0"
                                          value={med.precio_unitario || 0}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx] = { ...newMeds[idx] }; // Clonar objeto
                                            const unitario = parseFloat(e.target.value) || 0;
                                            newMeds[idx].precio_unitario = unitario;
                                            
                                            // Recalcular total
                                            const cant = parseFloat(newMeds[idx].cantidad_comprada) || 0;
                                            if (cant > 0) {
                                              newMeds[idx].precio_total = parseFloat((cant * unitario).toFixed(2));
                                            }
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                          className="text-end"
                                        />
                                      </td>
                                      <td>
                                        <Input
                                          type="number"
                                          size="sm"
                                          step="0.01"
                                          min="0"
                                          value={med.precio_total || 0}
                                          onChange={(e) => {
                                            const newMeds = [...medicamentosExtraidos];
                                            newMeds[idx] = { ...newMeds[idx] }; // Clonar objeto
                                            const total = parseFloat(e.target.value) || 0;
                                            newMeds[idx].precio_total = total;
                                            
                                            // Recalcular unitario implícito
                                            const cant = parseFloat(newMeds[idx].cantidad_comprada) || 1;
                                            if (cant > 0) {
                                              newMeds[idx].precio_unitario = total / cant;
                                            }
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                          className="text-end"
                                        />
                                      </td>
                                      <td className="text-center">
                                        <Button
                                          color="danger"
                                          size="sm"
                                          outline
                                          onClick={() => {
                                            const newMeds = medicamentosExtraidos.filter((_, i) => i !== idx);
                                            setMedicamentosExtraidos(newMeds);
                                          }}
                                          title="Eliminar medicamento"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                          
                          <div className="mt-2 text-muted small">
                            <i className="ri-information-line me-1"></i>
                            Revisa y edita los medicamentos extraídos. Campos requeridos: Nombre y Precio Total.
                          </div>
                          </div>
                        </Collapse>
                      </Col>
                    </Row>
                  )}

                  <div className="d-flex flex-column flex-md-row justify-content-end align-items-start mt-4">
                    {solicitud.estatus !== "Cancelado" && (
                      <div className="d-flex gap-2 mt-2 mt-md-0">
                        {isEditing && (
                          <Button
                            color="light"
                            className="d-flex align-items-center"
                            onClick={() => {
                              resetForm();
                              setShowFacturacion(true);
                            }}
                          >
                            <i className="ri-close-line me-1"></i>
                            Cancelar
                          </Button>
                        )}
                        <Button
                          type="button"
                          color="success"
                          className="btn-lg px-4"
                          id="registrarFacturacionTooltip"
                          disabled={
                            loadingx || 
                            validacionFactura.estado === "duplicada" ||
                            !!fechaEntregaError || 
                            !!alertaFecha || 
                            !!formErrors.fechaFacturacion
                          }
                          onClick={handleSubmitFacturaion}
                        >
                          {loadingx ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Registrando...
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line me-1"></i>
                              {isEditing ? "Actualizar" : "Registrar"}{" "}
                              Facturación
                            </>
                          )}
                        </Button>
                        <Tooltip
                          placement="top"
                          isOpen={tooltipOpen.registrarFacturacionTooltip}
                          target="registrarFacturacionTooltip"
                          toggle={() => toggleTooltip('registrarFacturacionTooltip')}
                        >
                          {formErrors.fechaFacturacion
                            ? "Corrige el error de fecha antes de registrar"
                            : alertaFecha
                            ? "Confirma o corrige la fecha fuera del mes actual antes de registrar"
                            : fechaEntregaError 
                            ? "Corrige la fecha de la factura antes de guardar"
                            : isEditing ? "Actualizar facturación" : "Registrar facturación"}
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
                      )}
                    </>
                  )}
                </>
              )}
            </Form>
          </CardBody>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="d-flex align-items-center">
              <i className="ri-check-double-line me-1 text-primary me-2 fs-20"></i>
              <h5 className="card-title mb-0">Información de Facturación:</h5>
            </div>
          </CardHeader>

          <CardBody className="p-4">
            {/* Banner de permiso pendiente por monto excedente */}
            {permisoExcedentePendiente && (
              <div className="alert alert-warning d-flex align-items-start mb-3" role="alert">
                <i className="ri-mail-send-line me-2 fs-18 mt-1"></i>
                <div className="flex-grow-1">
                  <strong>Permiso de Seguros Mercantil pendiente</strong>
                  <p className="mb-2 mt-1">
                    La factura registrada supera el monto de cobertura. Se debe enviar un correo a Seguros Mercantil
                    solicitando permiso para procesar esta solicitud con el monto excedente.
                  </p>
                  <p className="mb-0 small text-muted">
                    Solicitado por: {facturaConExcedentePendiente?.permisoExcedente?.usuarioSolicitante} |{" "}
                    Monto facturado: {facturaConExcedentePendiente?.montoTotal?.toFixed(2)} Bs |{" "}
                    Cobertura máxima: {facturaConExcedentePendiente?.permisoExcedente?.montoMaximoBs?.toFixed(2)} Bs
                  </p>
                </div>
                <Button
                  color="success"
                  size="sm"
                  className="ms-3 flex-shrink-0"
                  onClick={() => setModalPermisoOpen(true)}
                >
                  <i className="ri-check-double-line me-1"></i>
                  Ya se tiene el permiso
                </Button>
              </div>
            )}

            <FacturacionTable
              solicitud={solicitud}
              onEdit={editarSolicitudFacturacion}
            />
          </CardBody>
        </Card>
      )}

      <FacturaModal
        isOpen={modalIsOpen}
        toggle={() => setModalIsOpen(false)}
        imageUrl={facturaImageUrl}
      />

      {/* Modal de confirmación para monto excedente */}
      <Modal isOpen={modalExcedenteOpen} toggle={() => setModalExcedenteOpen(false)} centered>
        <ModalHeader toggle={() => setModalExcedenteOpen(false)}>
          <i className="ri-error-warning-line text-warning me-2"></i>
          Monto excede la cobertura
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-warning mb-3">
            <p className="mb-0">{mensajeExcedente}</p>
          </div>
          <p>
            Para confirmar una factura con este monto, debe solicitar permiso por correo
            a <strong>Seguros Mercantil</strong>.
          </p>
          <p className="mb-0">Al aceptar:</p>
          <ul>
            <li>La factura se registrará normalmente</li>
            <li>La solicitud quedará bloqueada hasta adjuntar la respuesta de aprobación</li>
            <li>Usted debe enviar el correo solicitando el permiso por su cuenta</li>
          </ul>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModalExcedenteOpen(false)}>
            Cancelar
          </Button>
          <Button color="warning" onClick={handleConfirmExcedente}>
            <i className="ri-mail-send-line me-1"></i>
            Acepto, enviaré el correo
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal para adjuntar captura de permiso de Seguros Mercantil */}
      <Modal isOpen={modalPermisoOpen} toggle={() => !loadingPermiso && setModalPermisoOpen(false)} centered>
        <ModalHeader toggle={() => !loadingPermiso && setModalPermisoOpen(false)}>
          <i className="ri-mail-check-line text-success me-2"></i>
          Adjuntar permiso de Seguros Mercantil
        </ModalHeader>
        <ModalBody>
          <p>
            Adjunte la captura del correo de respuesta de Seguros Mercantil
            otorgando el permiso para procesar esta solicitud.
          </p>
          <FormGroup>
            <Label className="form-label fw-medium">
              Captura del correo de aprobación <span className="text-danger">*</span>
            </Label>
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={handleArchivoPermiso}
              disabled={loadingPermiso}
            />
            <small className="text-muted">Formatos aceptados: imágenes o PDF (máx. 5MB)</small>
          </FormGroup>
          {previewPermiso && (
            <div className="text-center mt-3 border rounded p-2">
              <img
                src={previewPermiso}
                alt="Preview del permiso"
                style={{ maxWidth: "100%", maxHeight: "300px", objectFit: "contain" }}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => { setModalPermisoOpen(false); setArchivoPermiso(null); setPreviewPermiso(null); }} disabled={loadingPermiso}>
            Cancelar
          </Button>
          <Button color="success" onClick={handleSubirPermiso} disabled={!archivoPermiso || loadingPermiso}>
            {loadingPermiso ? (
              <>
                <Spinner size="sm" className="me-2" />
                Procesando...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1"></i>
                Confirmar permiso recibido
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de advertencia de razón social */}
      <Modal 
        isOpen={modalRazonSocialOpen} 
        toggle={() => setModalRazonSocialOpen(false)} 
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setModalRazonSocialOpen(false)}>
          <i className="ri-alert-line text-warning me-2"></i>
          Advertencia: Razón Social no coincide
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-warning mb-3">
            <div className="d-flex align-items-start">
              <i className="ri-error-warning-line fs-20 me-2"></i>
              <div>
                <h6 className="mb-2">La razón social de la factura no corresponde a la aseguradora de esta solicitud</h6>
                <p className="mb-0 small">
                  Por favor, verifica que la factura pertenezca a la aseguradora correcta antes de continuar.
                </p>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered mb-0">
              <tbody>
                <tr>
                  <td className="fw-semibold bg-light" style={{ width: '40%' }}>
                    <i className="ri-building-line me-2 text-primary"></i>
                    Aseguradora de la solicitud:
                  </td>
                  <td>
                    <span className="badge bg-primary-subtle text-primary fs-14">
                      {solicitud?.aseguradora}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="fw-semibold bg-light">
                    <i className="ri-check-line me-2 text-success"></i>
                    Razón social esperada:
                  </td>
                  <td>
                    <span className="text-success fw-medium font-monospace">
                      {razonSocialMismatch?.razonEsperada}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="fw-semibold bg-light">
                    <i className="ri-close-line me-2 text-danger"></i>
                    Razón social encontrada:
                  </td>
                  <td>
                    <span className="text-danger fw-medium font-monospace">
                      {razonSocialMismatch?.razonEncontrada}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="alert alert-info mt-3 mb-0">
            <i className="ri-information-line me-2"></i>
            <small>
              <strong>¿Qué hacer?</strong><br/>
              • Si la factura es correcta y pertenece a <strong>{solicitud?.aseguradora}</strong>, puedes continuar.<br/>
              • Si la factura pertenece a otra aseguradora, cancela y verifica la solicitud correcta.
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="light" 
            onClick={() => {
              setModalRazonSocialOpen(false);
              setRazonSocialMismatch(null);
              // Limpiar formulario y volver a estado inicial
              resetForm();
              limpiarEstadosError("idle");
            }}
          >
            <i className="ri-close-line me-1"></i>
            Cancelar y revisar
          </Button>
          <Button 
            color="warning" 
            onClick={() => {
              setModalRazonSocialOpen(false);
              // Permitir continuar con la advertencia
              toast.info(
                " Continuando con advertencia de razón social. Verifica los datos antes de guardar.",
                { autoClose: 5000 }
              );
            }}
          >
            <i className="ri-check-line me-1"></i>
            Continuar de todas formas
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  );
};

export default Facturacion;
